import React from 'react';
import { BRAND, localePath } from '../../../config/region';

/*
 * "how-to-write-vn-scenario" in every language it has been translated into.
 *
 * Shared fields (slug, dates) sit at the top level; each language supplies only what differs.
 * Russian is the authored original. A language that is missing here falls back to English, or to
 * Russian in the russia build — see blogPosts/index.js.
 */
const post = {
  slug: 'how-to-write-vn-scenario',
  datePublished: '2025-06-01',
  dateModified: '2026-03-01',

  russian: {
    title: '✍️ Как написать сценарий для визуальной новеллы: пошаговое руководство',
    description: 'Мечтаете создать собственную визуальную новеллу? Это руководство проведёт вас через весь проц',
    seoTitle: `Как написать сценарий для визуальной новеллы: пошаговое руководство — ${BRAND}`,
    seoDescription: 'Мечтаете создать собственную визуальную новеллу? Это руководство проведёт вас через весь процесс — от идеи до готового сценария.',
    content: (
      <>
        <p>Мечтаете создать собственную визуальную новеллу? Это руководство проведёт вас через весь процесс — от идеи до готового сценария. А если вы хотите сразу увидеть свою историю в действии, платформа <a href={localePath('/')}>{BRAND}</a> позволяет создавать новеллы без программирования.</p>

        <h2>Что такое сценарий визуальной новеллы?</h2>

        <p>Сценарий VN отличается от сценария фильма или книги:</p>

        <ul>
          <li><strong>Диалоговая структура</strong> — большая часть текста — это реплики персонажей</li>
          <li><strong>Ветвление</strong> — история разделяется в зависимости от выборов игрока</li>
          <li><strong>Несколько концовок</strong> — разные финалы для разных путей</li>
          <li><strong>Технические пометки</strong> — указания для художников, программистов, композиторов</li>
        </ul>

        <h2>Этап 1: Концепция</h2>

        <h3>Определите жанр</h3>

        <p>Выберите основной жанр вашей новеллы:</p>

        <ul>
          <li><strong>Романтика</strong> — история любви с развитием отношений</li>
          <li><strong>Детектив</strong> — расследование с загадками</li>
          <li><strong>Хоррор</strong> — пугающая атмосфера и напряжение</li>
          <li><strong>Фэнтези</strong> — магические миры и приключения</li>
          <li><strong>Slice of Life</strong> — повседневная жизнь и дружба</li>
          <li><strong>Научная фантастика</strong> — технологии и будущее</li>
        </ul>

        <h3>Придумайте логлайн</h3>

        <p>Логлайн — это суть истории в 1-2 предложениях:</p>

        <blockquote><em>Студент магической академии обнаруживает заговор среди преподавателей и должен выбрать: разоблачить их и рискнуть жизнью друзей или промолчать.</em></blockquote>

        <blockquote><em>Девушка переезжает в новый город, где знакомится с тремя парнями — каждый скрывает тайну, связанную с её прошлым.</em></blockquote>

        <h3>Определите тон и атмосферу</h3>

        <ul>
          <li>Серьёзный или комедийный?</li>
          <li>Светлый или мрачный?</li>
          <li>Реалистичный или стилизованный?</li>
        </ul>

        <h2>Этап 2: Мир и сеттинг</h2>

        <h3>Создайте место действия</h3>

        <p><strong>Вопросы для проработки:</strong></p>
        <ul>
          <li>Где происходит история? (страна, город, эпоха)</li>
          <li>Какие особенности у этого мира? (магия, технологии, социум)</li>
          <li>Какие локации посетит игрок?</li>
        </ul>

        <p><strong>Пример локаций для школьной новеллы:</strong></p>
        <ul>
          <li>Классная комната</li>
          <li>Крыша школы</li>
          <li>Клубная комната</li>
          <li>Дом главного героя</li>
          <li>Кафе в городе</li>
        </ul>

        <h3>Установите правила мира</h3>

        <p>Если в истории есть фантастические элементы, определите их границы:</p>
        <ul>
          <li>Как работает магия?</li>
          <li>Какие есть ограничения?</li>
          <li>Что возможно, а что нет?</li>
        </ul>

        <h2>Этап 3: Персонажи</h2>

        <h3>Главный герой</h3>

        <p>В визуальных новеллах игрок отождествляет себя с протагонистом:</p>

        <p><strong>Прописать:</strong></p>
        <ul>
          <li>Имя и возраст</li>
          <li>Внешность (если показывается)</li>
          <li>Характер и личность</li>
          <li>Мотивация — чего хочет добиться</li>
          <li>Изъяны — что делает его человечным</li>
          <li>Арка развития — как изменится к финалу</li>
        </ul>

        <h3>Ключевые персонажи</h3>

        <p>Для каждого важного персонажа создайте профиль:</p>

        <pre><code>Имя: Алиса Морозова
  Возраст: 17 лет
  Роль: Романтический интерес / помощница
  Внешность: Чёрные волосы до плеч, зелёные глаза
  Характер: Сдержанная, умная, скрытная
  Секрет: Знает правду о смерти родителей главного героя
  Отношения с ГГ: Одноклассница, держит дистанцию</code></pre>

        <h3>Типы персонажей</h3>

        <ul>
          <li><strong>Протагонист</strong> — главный герой (часто без голоса)</li>
          <li><strong>Романтические интересы</strong> — потенциальные партнёры</li>
          <li><strong>Союзники</strong> — друзья и помощники</li>
          <li><strong>Антагонисты</strong> — противники и препятствия</li>
          <li><strong>Второстепенные</strong> — учителя, родственники, массовка</li>
        </ul>

        <h2>Этап 4: Структура сюжета</h2>

        <h3>Классическая трёхактная структура</h3>

        <p><strong>Акт 1: Завязка (20-25%)</strong></p>
        <ul>
          <li>Знакомство с героем и миром</li>
          <li>Инцидент, который запускает сюжет</li>
          <li>Герой принимает «вызов»</li>
        </ul>

        <p><strong>Акт 2: Развитие (50-60%)</strong></p>
        <ul>
          <li>Герой сталкивается с препятствиями</li>
          <li>Развиваются отношения с персонажами</li>
          <li>Ключевые выборы игрока</li>
          <li>Точка невозврата</li>
        </ul>

        <p><strong>Акт 3: Кульминация и развязка (20-25%)</strong></p>
        <ul>
          <li>Финальное противостояние</li>
          <li>Раскрытие тайн</li>
          <li>Концовка (зависит от выборов)</li>
        </ul>

        <h3>Система ветвления</h3>

        <p><strong>Линейная структура с развилками:</strong></p>
        <pre><code>Начало {'→'} Выбор 1 {'→'} Событие А {'→'} Выбор 2 {'→'} Концовка 1/2/3
                 {'→'} Событие Б {'→'} Выбор 3 {'→'} Концовка 4/5</code></pre>

        <p><strong>Маршрутная система:</strong></p>
        <pre><code>Общий пролог {'→'} Выбор персонажа {'→'} Маршрут Алисы {'→'} Концовки Алисы
                               {'→'} Маршрут Дмитрия {'→'} Концовки Дмитрия
                               {'→'} Маршрут Елены {'→'} Концовки Елены</code></pre>

        <h3>Типы выборов</h3>

        <ul>
          <li><strong>Сюжетные</strong> — влияют на развитие истории</li>
          <li><strong>Характерные</strong> — определяют личность героя</li>
          <li><strong>Романтические</strong> — развивают отношения с персонажем</li>
          <li><strong>Моральные</strong> — проверяют ценности игрока</li>
          <li><strong>Косметические</strong> — не влияют на сюжет, но добавляют иммерсии</li>
        </ul>

        <h2>Этап 5: Написание сценария</h2>

        <h3>Формат сценария VN</h3>

        <pre><code>[Локация: Школьный класс, день]
  [Музыка: school_calm.ogg]

  Алиса стоит у окна, глядя на осенний двор.

  АЛИСА (задумчиво)
  "Знаешь... иногда мне кажется, что всё это — просто сон."

  [Спрайт Алисы меняется на грустный]

  {'>'} Выбор:
  1. "О чём ты?" {'→'} goto scene_question
  2. "Я понимаю, о чём ты." {'→'} goto scene_understand
  3. [Обнять её] {'→'} goto scene_hug (требуется: affection {'>'}= 5)

  === scene_question ===
  ПРОТАГОНИСТ
  "О чём ты говоришь?"

  АЛИСА (удивлённо)
  "Ни о чём. Забудь."

  [Алиса отворачивается]</code></pre>

        <h3>Советы по написанию диалогов</h3>

        <p><strong>Делайте реплики естественными:</strong></p>
        <ul>
          <li>❌ «Я испытываю грусть из-за произошедшего события»</li>
          <li>✅ «Мне... плохо. Не могу перестать думать об этом.»</li>
        </ul>

        <p><strong>Показывайте характер через речь:</strong></p>
        <ul>
          <li>Грубый персонаж говорит короткими фразами</li>
          <li>Интеллектуал использует сложные слова</li>
          <li>Застенчивый часто запинается</li>
        </ul>

        <p><strong>Избегайте «talking heads»:</strong></p>
        <ul>
          <li>Добавляйте действия между репликами</li>
          <li>Описывайте реакции персонажей</li>
          <li>Используйте окружение</li>
        </ul>

        <h3>Технические пометки</h3>

        <p>Для команды разработки добавляйте:</p>

        <pre><code>[CG: first_kiss.png] — специальная иллюстрация
  [SFX: door_slam.wav] — звуковой эффект
  [Экран трясётся] — визуальный эффект
  [Переменная: trust_алиса +1] — изменение статистики</code></pre>

        <h2>Этап 6: Концовки</h2>

        <h3>Типы концовок</h3>

        <ul>
          <li><strong>True End</strong> — «истинная» концовка, раскрывающая всю историю</li>
          <li><strong>Good End</strong> — счастливый финал для конкретного маршрута</li>
          <li><strong>Normal End</strong> — нейтральный финал</li>
          <li><strong>Bad End</strong> — негативный исход</li>
          <li><strong>Secret End</strong> — скрытая концовка за особые условия</li>
        </ul>

        <h3>Как сделать концовки удовлетворительными</h3>

        <ol>
          <li><strong>Каждая концовка должна иметь смысл</strong> — не наказывайте игрока за «неправильные» выборы</li>
          <li><strong>Закрывайте сюжетные линии</strong> — отвечайте на поставленные вопросы</li>
          <li><strong>Эмоциональный резонанс</strong> — финал должен вызывать чувства</li>
          <li><strong>Награда за исследование</strong> — хорошие концовки требуют усилий</li>
        </ol>

        <h2>Создание новелл на {BRAND}</h2>

        <p>Если вы не хотите заниматься программированием и работой с движками, платформа <a href={localePath('/')}>{BRAND}</a> предлагает альтернативный подход:</p>

        <h3>Как это работает</h3>

        <ol>
          <li><strong>Создайте сеттинг</strong> — опишите мир вашей новеллы</li>
          <li><strong>Задайте персонажей</strong> — их характеры и роли</li>
          <li><strong>Установите правила</strong> — что может происходить в истории</li>
          <li><strong>ИИ оживит мир</strong> — искусственный интеллект будет вести историю</li>
        </ol>

        <h3>Преимущества платформы</h3>

        <ul>
          <li><strong>Без программирования</strong> — не нужно знать Ren'Py или Unity</li>
          <li><strong>Мгновенный результат</strong> — история работает сразу</li>
          <li><strong>Бесконечный контент</strong> — ИИ генерирует новые события</li>
          <li><strong>Тестирование идей</strong> — проверьте концепцию перед полной разработкой</li>
        </ul>

        <h3>Для кого подходит</h3>

        <ul>
          <li>Начинающим авторам, которые хотят попробовать свои идеи</li>
          <li>Писателям, желающим протестировать сеттинг</li>
          <li>Тем, кто хочет создать новеллу без команды</li>
        </ul>

        <h2>Инструменты для традиционной разработки</h2>

        <h3>Движки визуальных новелл</h3>

        <p><strong>Ren'Py</strong> (бесплатный)</p>
        <ul>
          <li>Самый популярный движок для VN</li>
          <li>Язык Python-подобный</li>
          <li>Большое сообщество</li>
        </ul>

        <p><strong>TyranoBuilder</strong> (платный)</p>
        <ul>
          <li>Визуальный редактор</li>
          <li>Не требует программирования</li>
          <li>Экспорт на разные платформы</li>
        </ul>

        <p><strong>Visual Novel Maker</strong> (платный)</p>
        <ul>
          <li>Редактор от RPG Maker</li>
          <li>Простой интерфейс</li>
          <li>Хорошая документация</li>
        </ul>

        <h3>Программы для написания</h3>

        <ul>
          <li><strong>Twine</strong> — визуальное планирование веток</li>
          <li><strong>Scrivener</strong> — организация больших текстов</li>
          <li><strong>Google Docs</strong> — совместная работа</li>
        </ul>

        <h2>Чек-лист перед началом работы</h2>

        <ul>
          <li>Определён жанр и тон</li>
          <li>Написан логлайн</li>
          <li>Проработан сеттинг</li>
          <li>Созданы профили персонажей</li>
          <li>Составлена структура сюжета</li>
          <li>Продумана система ветвления</li>
          <li>Определены типы концовок</li>
          <li>Выбран инструмент разработки</li>
        </ul>

        <h2>Заключение</h2>

        <p>Написание сценария для визуальной новеллы — это увлекательный творческий процесс. От идеи до готовой истории может пройти от нескольких недель до нескольких лет в зависимости от масштаба проекта.</p>

        <p>Если вы только начинаете, попробуйте создать короткую новеллу на 1-2 часа игрового времени. Или воспользуйтесь платформой <a href={localePath('/')}>{BRAND}</a>, чтобы сразу увидеть свои идеи в действии — без программирования и месяцев разработки.</p>

        <p><strong>Ваша история ждёт, чтобы быть рассказанной.</strong></p>
      </>
    ),
  },

  english: {
    title: '✍️ How to write a visual novel script: step-by-step guide',
    description: 'Dream of creating your own visual novel? This guide takes you through the entire process — from idea to finished script.',
    seoTitle: `How to write a visual novel script: step-by-step guide — ${BRAND}`,
    seoDescription: 'Dream of creating your own visual novel? This guide takes you through the entire process — from idea to finished script.',
    content: (
      <>
        <p>Dream of creating your own visual novel? This guide takes you through the entire process — from idea to finished script. And if you want to see your story in action right away, the platform <a href={localePath('/')}>{BRAND}</a> lets you create visual novels without programming.</p>

        <h2>What is a visual novel script?</h2>

        <p>A VN script differs from a film or book script:</p>

        <ul>
          <li><strong>Dialogue-driven structure</strong> — most of the text is character dialogue</li>
          <li><strong>Branching</strong> — the story splits based on player choices</li>
          <li><strong>Multiple endings</strong> — different conclusions for different paths</li>
          <li><strong>Technical notes</strong> — instructions for artists, programmers, composers</li>
        </ul>

        <h2>Stage 1: Concept</h2>

        <h3>Define the genre</h3>

        <p>Choose the main genre of your novel:</p>

        <ul>
          <li><strong>Romance</strong> — a love story with relationship development</li>
          <li><strong>Detective</strong> — investigation with mysteries</li>
          <li><strong>Horror</strong> — scary atmosphere and tension</li>
          <li><strong>Fantasy</strong> — magical worlds and adventures</li>
          <li><strong>Slice of Life</strong> — everyday life and friendship</li>
          <li><strong>Science Fiction</strong> — technology and the future</li>
        </ul>

        <h3>Create a logline</h3>

        <p>A logline is the essence of your story in 1–2 sentences:</p>

        <blockquote><em>A magic academy student discovers a conspiracy among teachers and must choose: expose them and risk his friends&apos; lives, or stay silent.</em></blockquote>

        <blockquote><em>A girl moves to a new city and meets three guys — each hiding a secret connected to her past.</em></blockquote>

        <h3>Determine tone and atmosphere</h3>

        <ul>
          <li>Serious or comedic?</li>
          <li>Light or dark?</li>
          <li>Realistic or stylized?</li>
        </ul>

        <h2>Stage 2: World and setting</h2>

        <h3>Create the setting</h3>

        <p><strong>Questions to develop:</strong></p>
        <ul>
          <li>Where does the story take place? (country, city, era)</li>
          <li>What are the characteristics of this world? (magic, technology, society)</li>
          <li>Which locations will the player visit?</li>
        </ul>

        <p><strong>Example locations for a school story:</strong></p>
        <ul>
          <li>Classroom</li>
          <li>School rooftop</li>
          <li>Club room</li>
          <li>Main character&apos;s house</li>
          <li>Café in town</li>
        </ul>

        <h3>Establish world rules</h3>

        <p>If your story has fantastical elements, define their limits:</p>
        <ul>
          <li>How does magic work?</li>
          <li>What are the constraints?</li>
          <li>What&apos;s possible and what isn&apos;t?</li>
        </ul>

        <h2>Stage 3: Characters</h2>

        <h3>The protagonist</h3>

        <p>In visual novels, the player identifies with the protagonist:</p>

        <p><strong>Develop:</strong></p>
        <ul>
          <li>Name and age</li>
          <li>Appearance (if shown)</li>
          <li>Personality and traits</li>
          <li>Motivation — what they want to achieve</li>
          <li>Flaws — what makes them human</li>
          <li>Character arc — how they change by the end</li>
        </ul>

        <h3>Key characters</h3>

        <p>For each important character, create a profile:</p>

        <pre><code>Name: Alice Morozova
Age: 17
Role: Romantic interest / helper
Appearance: Black shoulder-length hair, green eyes
Personality: Reserved, intelligent, secretive
Secret: Knows the truth about the protagonist&apos;s parents&apos; death
Relationship with MC: Classmate, keeps distance</code></pre>

        <h3>Character types</h3>

        <ul>
          <li><strong>Protagonist</strong> — the main character (often voiceless)</li>
          <li><strong>Romantic interests</strong> — potential partners</li>
          <li><strong>Allies</strong> — friends and helpers</li>
          <li><strong>Antagonists</strong> — enemies and obstacles</li>
          <li><strong>Secondary</strong> — teachers, family, background characters</li>
        </ul>

        <h2>Stage 4: Plot structure</h2>

        <h3>Classical three-act structure</h3>

        <p><strong>Act 1: Setup (20–25%)</strong></p>
        <ul>
          <li>Introduce the character and world</li>
          <li>An incident that triggers the plot</li>
          <li>The protagonist accepts the &quot;call&quot;</li>
        </ul>

        <p><strong>Act 2: Development (50–60%)</strong></p>
        <ul>
          <li>The protagonist faces obstacles</li>
          <li>Relationships with characters develop</li>
          <li>Key player choices</li>
          <li>Point of no return</li>
        </ul>

        <p><strong>Act 3: Climax and resolution (20–25%)</strong></p>
        <ul>
          <li>Final confrontation</li>
          <li>Secrets revealed</li>
          <li>Ending (depends on choices)</li>
        </ul>

        <h3>Branching system</h3>

        <p><strong>Linear structure with branches:</strong></p>
        <pre><code>Start {'→'} Choice 1 {'→'} Event A {'→'} Choice 2 {'→'} Ending 1/2/3
             {'→'} Event B {'→'} Choice 3 {'→'} Ending 4/5</code></pre>

        <p><strong>Route system:</strong></p>
        <pre><code>Common prologue {'→'} Choose character {'→'} Alice&apos;s route {'→'} Alice&apos;s endings
                           {'→'} David&apos;s route {'→'} David&apos;s endings
                           {'→'} Elena&apos;s route {'→'} Elena&apos;s endings</code></pre>

        <h3>Types of choices</h3>

        <ul>
          <li><strong>Plot</strong> — affect the story development</li>
          <li><strong>Character</strong> — define the hero&apos;s personality</li>
          <li><strong>Romantic</strong> — develop relationships with characters</li>
          <li><strong>Moral</strong> — test the player&apos;s values</li>
          <li><strong>Cosmetic</strong> — don&apos;t affect the plot, but add immersion</li>
        </ul>

        <h2>Stage 5: Script writing</h2>

        <h3>VN script format</h3>

        <pre><code>[Location: School classroom, daytime]
[Music: school_calm.ogg]

Alice stands by the window, gazing at the autumn courtyard.

ALICE (thoughtfully)
&quot;Sometimes I think all this is just a dream.&quot;

[Alice&apos;s sprite changes to sad]

{'>'} Choice:
1. &quot;What do you mean?&quot; {'→'} goto scene_question
2. &quot;I understand what you mean.&quot; {'→'} goto scene_understand
3. [Hug her] {'→'} goto scene_hug (requires: affection {'>'}= 5)

=== scene_question ===
PROTAGONIST
&quot;What are you talking about?&quot;

ALICE (surprised)
&quot;Nothing. Forget it.&quot;

[Alice turns away]</code></pre>

        <h3>Dialogue writing tips</h3>

        <p><strong>Make dialogue natural:</strong></p>
        <ul>
          <li>❌ &quot;I am experiencing sadness due to the occurred event&quot;</li>
          <li>✅ &quot;I... feel awful. Can&apos;t stop thinking about it.&quot;</li>
        </ul>

        <p><strong>Show character through speech:</strong></p>
        <ul>
          <li>Rude character speaks in short phrases</li>
          <li>Intellectual uses complex words</li>
          <li>Shy person often stammers</li>
        </ul>

        <p><strong>Avoid &quot;talking heads&quot;:</strong></p>
        <ul>
          <li>Add actions between lines</li>
          <li>Describe character reactions</li>
          <li>Use the environment</li>
        </ul>

        <h3>Technical notes</h3>

        <p>Add to your script for the team:</p>

        <pre><code>[CG: first_kiss.png] — special illustration
[SFX: door_slam.wav] — sound effect
[Screen shakes] — visual effect
[Variable: trust_alice +1] — change statistics</code></pre>

        <h2>Stage 6: Endings</h2>

        <h3>Types of endings</h3>

        <ul>
          <li><strong>True End</strong> — the &quot;true&quot; ending that reveals the whole story</li>
          <li><strong>Good End</strong> — happy conclusion for a specific route</li>
          <li><strong>Normal End</strong> — neutral ending</li>
          <li><strong>Bad End</strong> — negative outcome</li>
          <li><strong>Secret End</strong> — hidden ending for special conditions</li>
        </ul>

        <h3>How to make satisfying endings</h3>

        <ol>
          <li><strong>Each ending should make sense</strong> — don&apos;t punish the player for &quot;wrong&quot; choices</li>
          <li><strong>Close plot threads</strong> — answer the questions you raised</li>
          <li><strong>Emotional resonance</strong> — the ending should stir emotions</li>
          <li><strong>Reward exploration</strong> — good endings require effort</li>
        </ol>

        <h2>Creating novels on {BRAND}</h2>

        <p>If you don&apos;t want to deal with programming and game engines, <a href={localePath('/')}>{BRAND}</a> offers an alternative approach:</p>

        <h3>How it works</h3>

        <ol>
          <li><strong>Create a setting</strong> — describe your novel&apos;s world</li>
          <li><strong>Set up characters</strong> — their personalities and roles</li>
          <li><strong>Establish rules</strong> — what can happen in the story</li>
          <li><strong>AI brings it to life</strong> — artificial intelligence will drive the narrative</li>
        </ol>

        <h3>Platform advantages</h3>

        <ul>
          <li><strong>No programming</strong> — no need to know Ren&apos;Py or Unity</li>
          <li><strong>Instant results</strong> — your story works immediately</li>
          <li><strong>Endless content</strong> — AI generates new events</li>
          <li><strong>Test ideas</strong> — validate your concept before full development</li>
        </ul>

        <h3>Who it&apos;s for</h3>

        <ul>
          <li>Aspiring authors wanting to test their ideas</li>
          <li>Writers wanting to prototype a setting</li>
          <li>Anyone wanting to make a novel without a team</li>
        </ul>

        <h2>Tools for traditional development</h2>

        <h3>Visual novel engines</h3>

        <p><strong>Ren&apos;Py</strong> (free)</p>
        <ul>
          <li>Most popular VN engine</li>
          <li>Python-like scripting language</li>
          <li>Large community</li>
        </ul>

        <p><strong>TyranoBuilder</strong> (paid)</p>
        <ul>
          <li>Visual editor</li>
          <li>No programming required</li>
          <li>Export to multiple platforms</li>
        </ul>

        <p><strong>Visual Novel Maker</strong> (paid)</p>
        <ul>
          <li>Editor by RPG Maker</li>
          <li>Simple interface</li>
          <li>Good documentation</li>
        </ul>

        <h3>Writing software</h3>

        <ul>
          <li><strong>Twine</strong> — visual branch planning</li>
          <li><strong>Scrivener</strong> — organize large texts</li>
          <li><strong>Google Docs</strong> — collaborative writing</li>
        </ul>

        <h2>Checklist before you start</h2>

        <ul>
          <li>Genre and tone defined</li>
          <li>Logline written</li>
          <li>Setting developed</li>
          <li>Character profiles created</li>
          <li>Plot structure outlined</li>
          <li>Branching system planned</li>
          <li>Ending types determined</li>
          <li>Development tool chosen</li>
        </ul>

        <h2>Conclusion</h2>

        <p>Writing a visual novel script is an exciting creative process. From idea to finished story can take weeks to years depending on the project&apos;s scope.</p>

        <p>If you&apos;re just starting, try creating a short novel for 1–2 hours of gameplay. Or use <a href={localePath('/')}>{BRAND}</a> to see your ideas come to life right away — no programming and no months of development.</p>

        <p><strong>Your story is waiting to be told.</strong></p>
      </>
    ),
  },

  german: {
    title: '✍️ Wie man ein Szenario für eine visuelle Novelle schreibt: Schritt-für-Schritt-Anleitung',
    description: 'Träumst du davon, deine eigene visuelle Novelle zu erschaffen? Diese Anleitung führt dich durch den gesamten Prozess — von der Idee bis zum fertigen Szenario.',
    seoTitle: `Wie man ein Szenario für eine visuelle Novelle schreibt: Schritt-für-Schritt-Anleitung — ${BRAND}`,
    seoDescription: 'Träumst du davon, deine eigene visuelle Novelle zu erschaffen? Diese Anleitung führt dich durch den gesamten Prozess — von der Idee bis zum fertigen Szenario.',
    content: (
      <>
        <p>Träumst du davon, deine eigene visuelle Novelle zu erschaffen? Diese Anleitung führt dich durch den gesamten Prozess — von der Idee bis zum fertigen Szenario. Und wenn du deine Geschichte sofort in Aktion sehen möchtest, ermöglicht dir die Plattform <a href={localePath('/')}>{BRAND}</a>, visuelle Novellen ohne Programmierung zu erstellen.</p>

        <h2>Was ist ein Szenario für eine visuelle Novelle?</h2>

        <p>Ein VN-Szenario unterscheidet sich von einem Film- oder Buchszenario:</p>

        <ul>
          <li><strong>Dialoggesteuerte Struktur</strong> — der Großteil des Textes besteht aus Charakterdialogen</li>
          <li><strong>Verzweigung</strong> — die Geschichte teilt sich je nach Spielerentscheidungen</li>
          <li><strong>Mehrere Enden</strong> — unterschiedliche Schlussfolgerungen für verschiedene Wege</li>
          <li><strong>Technische Notizen</strong> — Anweisungen für Künstler, Programmierer, Komponisten</li>
        </ul>

        <h2>Stufe 1: Konzept</h2>

        <h3>Definiere das Genre</h3>

        <p>Wähle das Hauptgenre deiner Novelle:</p>

        <ul>
          <li><strong>Romantik</strong> — eine Liebesgeschichte mit Beziehungsentwicklung</li>
          <li><strong>Detektiv</strong> — Ermittlung mit Rätseln</li>
          <li><strong>Horror</strong> — gruselige Atmosphäre und Spannung</li>
          <li><strong>Fantasy</strong> — magische Welten und Abenteuer</li>
          <li><strong>Slice of Life</strong> — Alltag und Freundschaft</li>
          <li><strong>Science-Fiction</strong> — Technologie und Zukunft</li>
        </ul>

        <h3>Erstelle einen Logline</h3>

        <p>Ein Logline ist die Essenz deiner Geschichte in 1–2 Sätzen:</p>

        <blockquote><em>Ein Schüler einer Magieschule entdeckt eine Verschwörung unter den Lehrern und muss sich entscheiden: Sie entlarven und das Leben seiner Freunde riskieren, oder schweigen.</em></blockquote>

        <blockquote><em>Ein Mädchen zieht in eine neue Stadt und trifft drei Jungs — jeder verbirgt ein Geheimnis, das mit ihrer Vergangenheit verbunden ist.</em></blockquote>

        <h3>Bestimme Ton und Atmosphäre</h3>

        <ul>
          <li>Ernst oder komisch?</li>
          <li>Hell oder dunkel?</li>
          <li>Realistisch oder stilisiert?</li>
        </ul>

        <h2>Stufe 2: Welt und Szenario</h2>

        <h3>Erschaffe das Setting</h3>

        <p><strong>Fragen zur Entwicklung:</strong></p>
        <ul>
          <li>Wo spielt die Geschichte? (Land, Stadt, Ära)</li>
          <li>Welche Besonderheiten hat diese Welt? (Magie, Technologie, Gesellschaft)</li>
          <li>Welche Orte wird der Spieler besuchen?</li>
        </ul>

        <p><strong>Beispielorte für eine Schulgeschichte:</strong></p>
        <ul>
          <li>Klassenzimmer</li>
          <li>Schuldach</li>
          <li>Clubraum</li>
          <li>Haus des Hauptcharakters</li>
          <li>Café in der Stadt</li>
        </ul>

        <h3>Etabliere Weltregeln</h3>

        <p>Wenn deine Geschichte fantastische Elemente hat, definiere ihre Grenzen:</p>
        <ul>
          <li>Wie funktioniert Magie?</li>
          <li>Was sind die Einschränkungen?</li>
          <li>Was ist möglich und was nicht?</li>
        </ul>

        <h2>Stufe 3: Charaktere</h2>

        <h3>Der Protagonist</h3>

        <p>In visuellen Novellen identifiziert sich der Spieler mit dem Protagonisten:</p>

        <p><strong>Entwickle:</strong></p>
        <ul>
          <li>Name und Alter</li>
          <li>Aussehen (falls gezeigt)</li>
          <li>Persönlichkeit und Eigenschaften</li>
          <li>Motivation — was wollen sie erreichen</li>
          <li>Fehler — was macht sie menschlich</li>
          <li>Charakterentwicklung — wie ändern sie sich bis zum Ende</li>
        </ul>

        <h3>Wichtige Charaktere</h3>

        <p>Für jeden wichtigen Charakter erstelle ein Profil:</p>

        <pre><code>Name: Alice Morozova
Alter: 17
Rolle: Romantisches Interesse / Helferin
Aussehen: Schwarze schulterlange Haare, grüne Augen
Persönlichkeit: Zurückhaltend, intelligent, geheimnisvoll
Geheimnis: Kennt die Wahrheit über den Tod der Eltern des Protagonisten
Verhältnis zu Protagonist: Klassenkameradin, hält Distanz</code></pre>

        <h3>Charaktertypen</h3>

        <ul>
          <li><strong>Protagonist</strong> — die Hauptfigur (oft stumm)</li>
          <li><strong>Romantische Interessen</strong> — potenzielle Partner</li>
          <li><strong>Verbündete</strong> — Freunde und Helfer</li>
          <li><strong>Antagonisten</strong> — Feinde und Hindernisse</li>
          <li><strong>Nebenfiguren</strong> — Lehrer, Familie, Statisten</li>
        </ul>

        <h2>Stufe 4: Handlungsstruktur</h2>

        <h3>Klassische dreiaktige Struktur</h3>

        <p><strong>Akt 1: Exposition (20–25%)</strong></p>
        <ul>
          <li>Führe den Charakter und die Welt ein</li>
          <li>Ein Zwischenfall, der die Handlung auslöst</li>
          <li>Der Protagonist nimmt den &quot;Ruf&quot; an</li>
        </ul>

        <p><strong>Akt 2: Entwicklung (50–60%)</strong></p>
        <ul>
          <li>Der Protagonist sieht sich Hindernissen gegenüber</li>
          <li>Beziehungen zu Charakteren entwickeln sich</li>
          <li>Wichtige Spielerentscheidungen</li>
          <li>Punkt ohne Rückkehr</li>
        </ul>

        <p><strong>Akt 3: Höhepunkt und Auflösung (20–25%)</strong></p>
        <ul>
          <li>Letzte Konfrontation</li>
          <li>Geheimnisse werden enthüllt</li>
          <li>Ende (hängt von den Entscheidungen ab)</li>
        </ul>

        <h3>Verzweigungssystem</h3>

        <p><strong>Lineare Struktur mit Ästen:</strong></p>
        <pre><code>Start {'→'} Wahl 1 {'→'} Ereignis A {'→'} Wahl 2 {'→'} Ending 1/2/3
          {'→'} Ereignis B {'→'} Wahl 3 {'→'} Ending 4/5</code></pre>

        <p><strong>Routensystem:</strong></p>
        <pre><code>Gemeinsamer Prolog {'→'} Charakter wählen {'→'} Alice&apos;s Route {'→'} Alice&apos;s Enden
                           {'→'} David&apos;s Route {'→'} David&apos;s Enden
                           {'→'} Elena&apos;s Route {'→'} Elena&apos;s Enden</code></pre>

        <h3>Arten von Entscheidungen</h3>

        <ul>
          <li><strong>Handlung</strong> — beeinflussen die Handlungsentwicklung</li>
          <li><strong>Charakter</strong> — definieren die Persönlichkeit des Helden</li>
          <li><strong>Romantik</strong> — entwickeln Beziehungen zu Charakteren</li>
          <li><strong>Moral</strong> — testen die Werte des Spielers</li>
          <li><strong>Kosmetik</strong> — beeinflussen die Handlung nicht, aber verstärken die Immersion</li>
        </ul>

        <h2>Stufe 5: Szenarioschreiben</h2>

        <h3>VN-Szenarioformat</h3>

        <pre><code>[Ort: Schulklassenzimmer, Tageszeit]
[Musik: school_calm.ogg]

Alice steht am Fenster und schaut auf den Herbsthof hinaus.

ALICE (nachdenklich)
&quot;Manchmal denke ich, dass das alles nur ein Traum ist.&quot;

[Alice Sprite wechselt zu traurig]

{'>'} Wahl:
1. &quot;Was meinst du?&quot; {'→'} goto scene_question
2. &quot;Ich verstehe, was du meinst.&quot; {'→'} goto scene_understand
3. [Umarme sie] {'→'} goto scene_hug (Anforderung: affection {'>'}= 5)

=== scene_question ===
PROTAGONIST
&quot;Wovon redest du?&quot;

ALICE (überrascht)
&quot;Nichts. Vergiss es.&quot;

[Alice wendet sich ab]</code></pre>

        <h3>Tipps zum Schreiben von Dialogen</h3>

        <p><strong>Mache Dialoge natürlich:</strong></p>
        <ul>
          <li>❌ &quot;Ich erlebe Traurigkeit aufgrund des eingetretenen Ereignisses&quot;</li>
          <li>✅ &quot;Mir... geht es nicht gut. Kann nicht aufhören, daran zu denken.&quot;</li>
        </ul>

        <p><strong>Zeige den Charakter durch die Rede:</strong></p>
        <ul>
          <li>Grober Charakter spricht in kurzen Sätzen</li>
          <li>Intellektueller benutzt komplexe Wörter</li>
          <li>Schüchterner stammelt oft</li>
        </ul>

        <p><strong>Vermeide &quot;Talking Heads&quot;:</strong></p>
        <ul>
          <li>Füge zwischen Repliken Handlungen hinzu</li>
          <li>Beschreibe Charakterreaktionen</li>
          <li>Nutze die Umgebung</li>
        </ul>

        <h3>Technische Notizen</h3>

        <p>Füge für dein Team hinzu:</p>

        <pre><code>[CG: first_kiss.png] — spezielle Illustration
[SFX: door_slam.wav] — Soundeffekt
[Bildschirm wackelt] — visueller Effekt
[Variable: trust_alice +1] — Statistik ändern</code></pre>

        <h2>Stufe 6: Enden</h2>

        <h3>Arten von Enden</h3>

        <ul>
          <li><strong>True End</strong> — das &quot;wahre&quot; Ende, das die ganze Geschichte enthüllt</li>
          <li><strong>Good End</strong> — glückliches Ending für eine bestimmte Route</li>
          <li><strong>Normal End</strong> — neutrales Ending</li>
          <li><strong>Bad End</strong> — negativer Ausgang</li>
          <li><strong>Secret End</strong> — verstecktes Ending für spezielle Bedingungen</li>
        </ul>

        <h3>Wie man befriedigende Enden macht</h3>

        <ol>
          <li><strong>Jedes Ende sollte sinnvoll sein</strong> — bestrafe den Spieler nicht für &quot;falsche&quot; Entscheidungen</li>
          <li><strong>Schließe Handlungsstränge ab</strong> — beantworte die Fragen, die du gestellt hast</li>
          <li><strong>Emotionale Resonanz</strong> — das Ende sollte Gefühle hervorrufen</li>
          <li><strong>Belohne Erkundung</strong> — gute Enden erfordern Anstrengung</li>
        </ol>

        <h2>Novellen auf {BRAND} erstellen</h2>

        <p>Wenn du dich nicht mit Programmierung und Spielengines auseinandersetzen möchtest, bietet <a href={localePath('/')}>{BRAND}</a> einen alternativen Ansatz:</p>

        <h3>So funktioniert es</h3>

        <ol>
          <li><strong>Erschaffe ein Setting</strong> — beschreibe die Welt deiner Novelle</li>
          <li><strong>Richte Charaktere ein</strong> — ihre Persönlichkeiten und Rollen</li>
          <li><strong>Etabliere Regeln</strong> — was in der Geschichte passieren kann</li>
          <li><strong>KI bringt es zum Leben</strong> — künstliche Intelligenz wird die Erzählung vorantreiben</li>
        </ol>

        <h3>Plattformvorteile</h3>

        <ul>
          <li><strong>Keine Programmierung</strong> — musst Ren&apos;Py oder Unity nicht kennen</li>
          <li><strong>Sofortige Ergebnisse</strong> — deine Geschichte funktioniert sofort</li>
          <li><strong>Unendliche Inhalte</strong> — KI generiert neue Ereignisse</li>
          <li><strong>Teste Ideen</strong> — validiere dein Konzept vor vollständiger Entwicklung</li>
        </ul>

        <h3>Wofür es geeignet ist</h3>

        <ul>
          <li>Angehende Autoren, die ihre Ideen testen möchten</li>
          <li>Schriftsteller, die ein Setting prototypisieren möchten</li>
          <li>Jeder, der eine Novelle ohne Team machen möchte</li>
        </ul>

        <h2>Werkzeuge für traditionelle Entwicklung</h2>

        <h3>Visuelle Novel Engines</h3>

        <p><strong>Ren&apos;Py</strong> (kostenlos)</p>
        <ul>
          <li>Beliebteste VN Engine</li>
          <li>Python-ähnliche Skriptsprache</li>
          <li>Große Gemeinschaft</li>
        </ul>

        <p><strong>TyranoBuilder</strong> (bezahlt)</p>
        <ul>
          <li>Visueller Editor</li>
          <li>Keine Programmierung erforderlich</li>
          <li>Export auf mehrere Plattformen</li>
        </ul>

        <p><strong>Visual Novel Maker</strong> (bezahlt)</p>
        <ul>
          <li>Editor von RPG Maker</li>
          <li>Einfache Schnittstelle</li>
          <li>Gute Dokumentation</li>
        </ul>

        <h3>Schreibsoftware</h3>

        <ul>
          <li><strong>Twine</strong> — visuelle Astplanierung</li>
          <li><strong>Scrivener</strong> — große Texte organisieren</li>
          <li><strong>Google Docs</strong> — kollaboratives Schreiben</li>
        </ul>

        <h2>Checkliste vor dem Start</h2>

        <ul>
          <li>Genre und Ton definiert</li>
          <li>Logline geschrieben</li>
          <li>Setting entwickelt</li>
          <li>Charakterprofile erstellt</li>
          <li>Handlungsstruktur übersichtlich</li>
          <li>Verzweigungssystem geplant</li>
          <li>Endtypen bestimmt</li>
          <li>Entwicklungswerkzeug ausgewählt</li>
        </ul>

        <h2>Fazit</h2>

        <p>Das Schreiben eines Szenarios für eine visuelle Novelle ist ein spannender kreativer Prozess. Von der Idee bis zur fertigen Geschichte kann es je nach Projektumfang von Wochen bis Jahren dauern.</p>

        <p>Wenn du gerade anfängst, versuche, eine kurze Novelle für 1–2 Stunden Spielzeit zu schreiben. Oder nutze <a href={localePath('/')}>{BRAND}</a>, um deine Ideen sofort zum Leben zu erwecken — ohne Programmierung und ohne Monate Entwicklung.</p>

        <p><strong>Deine Geschichte wartet darauf, erzählt zu werden.</strong></p>
      </>
    ),
  },

  french: {
    title: '✍️ Comment écrire un scénario pour une roman visuel : guide étape par étape',
    description: "Rêves-tu de créer ton propre roman visuel ? Ce guide te guide à travers tout le processus — de l'idée au scénario finalisé.",
    seoTitle: `Comment écrire un scénario pour une roman visuel : guide étape par étape — ${BRAND}`,
    seoDescription: "Rêves-tu de créer ton propre roman visuel ? Ce guide te guide à travers tout le processus — de l'idée au scénario finalisé.",
    content: (
      <>
        <p>Rêves-tu de créer ton propre roman visuel ? Ce guide te guide à travers tout le processus — de l&apos;idée au scénario finalisé. Et si tu veux voir ton histoire en action tout de suite, la plateforme <a href={localePath('/')}>{BRAND}</a> te permet de créer des romans visuels sans programmation.</p>

        <h2>Qu&apos;est-ce qu&apos;un scénario de roman visuel ?</h2>

        <p>Un scénario VN diffère d&apos;un scénario de film ou de livre :</p>

        <ul>
          <li><strong>Structure basée sur le dialogue</strong> — la majeure partie du texte est un dialogue de personnages</li>
          <li><strong>Ramification</strong> — l&apos;histoire se divise selon les choix du joueur</li>
          <li><strong>Fins multiples</strong> — différentes conclusions pour différents chemins</li>
          <li><strong>Notes techniques</strong> — instructions pour les artistes, les programmeurs, les compositeurs</li>
        </ul>

        <h2>Étape 1 : Concept</h2>

        <h3>Définis le genre</h3>

        <p>Choisis le genre principal de ton roman :</p>

        <ul>
          <li><strong>Romance</strong> — une histoire d&apos;amour avec développement des relations</li>
          <li><strong>Détective</strong> — enquête avec mystères</li>
          <li><strong>Horreur</strong> — atmosphère effrayante et tension</li>
          <li><strong>Fantaisie</strong> — mondes magiques et aventures</li>
          <li><strong>Slice of Life</strong> — vie quotidienne et amitié</li>
          <li><strong>Science-fiction</strong> — technologie et avenir</li>
        </ul>

        <h3>Crée une logline</h3>

        <p>Une logline est l&apos;essence de ton histoire en 1–2 phrases :</p>

        <blockquote><em>Une étudiante d&apos;académie magique découvre une conspiration parmi les professeurs et doit choisir : les exposer et risquer la vie de ses amis, ou rester silencieuse.</em></blockquote>

        <blockquote><em>Une fille déménage dans une nouvelle ville et rencontre trois garçons — chacun cache un secret lié à son passé.</em></blockquote>

        <h3>Détermine le ton et l&apos;atmosphère</h3>

        <ul>
          <li>Sérieux ou comique ?</li>
          <li>Léger ou sombre ?</li>
          <li>Réaliste ou stylisé ?</li>
        </ul>

        <h2>Étape 2 : Le monde et le cadre</h2>

        <h3>Crée le cadre</h3>

        <p><strong>Questions à développer :</strong></p>
        <ul>
          <li>Où se déroule l&apos;histoire ? (pays, ville, époque)</li>
          <li>Quelles sont les caractéristiques de ce monde ? (magie, technologie, société)</li>
          <li>Quels lieux le joueur visitera-t-il ?</li>
        </ul>

        <p><strong>Exemples de lieux pour une histoire scolaire :</strong></p>
        <ul>
          <li>Salle de classe</li>
          <li>Toit de l&apos;école</li>
          <li>Salle de club</li>
          <li>Maison du personnage principal</li>
          <li>Café en ville</li>
        </ul>

        <h3>Établis les règles du monde</h3>

        <p>Si ton histoire a des éléments fantastiques, définis leurs limites :</p>
        <ul>
          <li>Comment fonctionne la magie ?</li>
          <li>Quelles sont les contraintes ?</li>
          <li>Qu&apos;est-ce qui est possible et ce qui ne l&apos;est pas ?</li>
        </ul>

        <h2>Étape 3 : Personnages</h2>

        <h3>Le protagoniste</h3>

        <p>Dans les romans visuels, le joueur s&apos;identifie au protagoniste :</p>

        <p><strong>Développe :</strong></p>
        <ul>
          <li>Nom et âge</li>
          <li>Apparence (si montré)</li>
          <li>Personnalité et traits</li>
          <li>Motivation — ce qu&apos;il veut réaliser</li>
          <li>Défauts — ce qui le rend humain</li>
          <li>Arc de caractère — comment il change à la fin</li>
        </ul>

        <h3>Personnages clés</h3>

        <p>Pour chaque personnage important, crée un profil :</p>

        <pre><code>Nom : Alice Morozova
Âge : 17
Rôle : Intérêt romantique / aide
Apparence : Cheveux noirs aux épaules, yeux verts
Personnalité : Réservée, intelligente, secrète
Secret : Connaît la vérité sur la mort des parents du protagoniste
Relation avec le protagoniste : Camarade de classe, garde ses distances</code></pre>

        <h3>Types de personnages</h3>

        <ul>
          <li><strong>Protagoniste</strong> — le personnage principal (souvent muet)</li>
          <li><strong>Intérêts romantiques</strong> — partenaires potentiels</li>
          <li><strong>Alliés</strong> — amis et aides</li>
          <li><strong>Antagonistes</strong> — ennemis et obstacles</li>
          <li><strong>Secondaires</strong> — enseignants, famille, personnages de fond</li>
        </ul>

        <h2>Étape 4 : Structure du scénario</h2>

        <h3>Structure classique en trois actes</h3>

        <p><strong>Acte 1 : Exposition (20–25 %)</strong></p>
        <ul>
          <li>Présente le personnage et le monde</li>
          <li>Un incident qui déclenche l&apos;intrigue</li>
          <li>Le protagoniste accepte l&apos;appel</li>
        </ul>

        <p><strong>Acte 2 : Développement (50–60 %)</strong></p>
        <ul>
          <li>Le protagoniste fait face à des obstacles</li>
          <li>Les relations avec les personnages se développent</li>
          <li>Choix clés du joueur</li>
          <li>Point de non-retour</li>
        </ul>

        <p><strong>Acte 3 : Climax et résolution (20–25 %)</strong></p>
        <ul>
          <li>Affrontement final</li>
          <li>Secrets révélés</li>
          <li>Fin (dépend des choix)</li>
        </ul>

        <h3>Système de ramification</h3>

        <p><strong>Structure linéaire avec ramifications :</strong></p>
        <pre><code>Début {'→'} Choix 1 {'→'} Événement A {'→'} Choix 2 {'→'} Fin 1/2/3
         {'→'} Événement B {'→'} Choix 3 {'→'} Fin 4/5</code></pre>

        <p><strong>Système de routes :</strong></p>
        <pre><code>Prologue commun {'→'} Choisir un personnage {'→'} Route d&apos;Alice {'→'} Fins d&apos;Alice
                           {'→'} Route de David {'→'} Fins de David
                           {'→'} Route d&apos;Elena {'→'} Fins d&apos;Elena</code></pre>

        <h3>Types de choix</h3>

        <ul>
          <li><strong>Intrigue</strong> — affectent le développement de l&apos;histoire</li>
          <li><strong>Caractère</strong> — définissent la personnalité du héros</li>
          <li><strong>Romantique</strong> — développent les relations avec les personnages</li>
          <li><strong>Moral</strong> — testent les valeurs du joueur</li>
          <li><strong>Cosmétique</strong> — n&apos;affectent pas l&apos;intrigue, mais ajoutent de l&apos;immersion</li>
        </ul>

        <h2>Étape 5 : Écriture du scénario</h2>

        <h3>Format de scénario VN</h3>

        <pre><code>[Lieu : Salle de classe, daytime]
[Musique : school_calm.ogg]

Alice se tient près de la fenêtre, regardant la cour d&apos;automne.

ALICE (pensivement)
« Parfois, je pense que tout cela n&apos;est qu&apos;un rêve. »

[Le sprite d&apos;Alice change pour triste]

{'>'} Choix:
1. « Qu&apos;est-ce que tu veux dire ? » {'→'} goto scene_question
2. « Je comprends ce que tu veux dire. » {'→'} goto scene_understand
3. [L&apos;étreindre] {'→'} goto scene_hug (requiert : affection {'>'}= 5)

=== scene_question ===
PROTAGONISTE
« De quoi parles-tu ? »

ALICE (surprise)
« Rien. Oublie. »

[Alice se détourne]</code></pre>

        <h3>Conseils pour écrire le dialogue</h3>

        <p><strong>Rends le dialogue naturel :</strong></p>
        <ul>
          <li>❌ « J&apos;éprouve de la tristesse en raison de l&apos;événement survenu »</li>
          <li>✅ « Je... me sens mal. Je ne peux pas arrêter d&apos;y penser. »</li>
        </ul>

        <p><strong>Montre le caractère par la parole :</strong></p>
        <ul>
          <li>Le personnage rude parle en phrases courtes</li>
          <li>L&apos;intellectuel utilise des mots complexes</li>
          <li>La personne timide bégaie souvent</li>
        </ul>

        <p><strong>Évite les « têtes parlantes » :</strong></p>
        <ul>
          <li>Ajoute des actions entre les répliques</li>
          <li>Décris les réactions des personnages</li>
          <li>Utilise l&apos;environnement</li>
        </ul>

        <h3>Notes techniques</h3>

        <p>Ajoute à ton scénario pour l&apos;équipe :</p>

        <pre><code>[CG : first_kiss.png] — illustration spéciale
[SFX : door_slam.wav] — effet sonore
[L&apos;écran tremble] — effet visuel
[Variable : trust_alice +1] — modifier les statistiques</code></pre>

        <h2>Étape 6 : Les fins</h2>

        <h3>Types de fins</h3>

        <ul>
          <li><strong>True End</strong> — la fin « vraie » qui révèle l&apos;histoire entière</li>
          <li><strong>Good End</strong> — conclusion heureuse pour une route spécifique</li>
          <li><strong>Normal End</strong> — fin neutre</li>
          <li><strong>Bad End</strong> — résultat négatif</li>
          <li><strong>Secret End</strong> — fin cachée pour les conditions spéciales</li>
        </ul>

        <h3>Comment faire des fins satisfaisantes</h3>

        <ol>
          <li><strong>Chaque fin devrait avoir du sens</strong> — ne punis pas le joueur pour les choix « incorrects »</li>
          <li><strong>Ferme les intrigues</strong> — réponds aux questions que tu as posées</li>
          <li><strong>Résonance émotionnelle</strong> — la fin devrait inspirer des émotions</li>
          <li><strong>Récompense l&apos;exploration</strong> — les bonnes fins nécessitent des efforts</li>
        </ol>

        <h2>Créer des romans sur {BRAND}</h2>

        <p>Si tu ne veux pas faire face à la programmation et aux moteurs de jeu, <a href={localePath('/')}>{BRAND}</a> offre une approche alternative :</p>

        <h3>Comment ça marche</h3>

        <ol>
          <li><strong>Crée un cadre</strong> — décris le monde de ton roman</li>
          <li><strong>Configure les personnages</strong> — leurs personnalités et rôles</li>
          <li><strong>Établis des règles</strong> — ce qui peut se passer dans l&apos;histoire</li>
          <li><strong>L&apos;IA l&apos;anime</strong> — l&apos;intelligence artificielle pilotera la narration</li>
        </ol>

        <h3>Avantages de la plateforme</h3>

        <ul>
          <li><strong>Pas de programmation</strong> — pas besoin de connaître Ren&apos;Py ou Unity</li>
          <li><strong>Résultats instantanés</strong> — ton histoire fonctionne immédiatement</li>
          <li><strong>Contenu infini</strong> — l&apos;IA génère de nouveaux événements</li>
          <li><strong>Teste des idées</strong> — valide ton concept avant le développement complet</li>
        </ul>

        <h3>Pour qui c&apos;est</h3>

        <ul>
          <li>Les auteurs en herbe qui veulent tester leurs idées</li>
          <li>Les écrivains qui veulent prototyper un cadre</li>
          <li>Quiconque veut faire un roman sans équipe</li>
        </ul>

        <h2>Outils pour le développement traditionnel</h2>

        <h3>Moteurs de romans visuels</h3>

        <p><strong>Ren&apos;Py</strong> (gratuit)</p>
        <ul>
          <li>Moteur VN le plus populaire</li>
          <li>Langage de script de type Python</li>
          <li>Grande communauté</li>
        </ul>

        <p><strong>TyranoBuilder</strong> (payant)</p>
        <ul>
          <li>Éditeur visuel</li>
          <li>Aucune programmation requise</li>
          <li>Exportation sur plusieurs plates-formes</li>
        </ul>

        <p><strong>Visual Novel Maker</strong> (payant)</p>
        <ul>
          <li>Éditeur de RPG Maker</li>
          <li>Interface simple</li>
          <li>Bonne documentation</li>
        </ul>

        <h3>Logiciels d&apos;écriture</h3>

        <ul>
          <li><strong>Twine</strong> — planification visuelle des branches</li>
          <li><strong>Scrivener</strong> — organisez de grands textes</li>
          <li><strong>Google Docs</strong> — écriture collaborative</li>
        </ul>

        <h2>Liste de contrôle avant de commencer</h2>

        <ul>
          <li>Genre et ton définis</li>
          <li>Logline écrite</li>
          <li>Cadre développé</li>
          <li>Profils de personnages créés</li>
          <li>Structure de l&apos;intrigue esquissée</li>
          <li>Système de ramification planifié</li>
          <li>Types de fins déterminés</li>
          <li>Outil de développement choisi</li>
        </ul>

        <h2>Conclusion</h2>

        <p>Écrire un scénario pour un roman visuel est un processus créatif passionnant. De l&apos;idée à l&apos;histoire terminée peut prendre de quelques semaines à plusieurs années selon l&apos;étendue du projet.</p>

        <p>Si tu commences tout juste, essaie de créer un court roman pour 1–2 heures de jeu. Ou utilise <a href={localePath('/')}>{BRAND}</a> pour voir tes idées prendre vie tout de suite — pas de programmation et pas de mois de développement.</p>

        <p><strong>Ton histoire attend d&apos;être contée.</strong></p>
      </>
    ),
  },

  spanish: {
    title: '✍️ Cómo escribir un guión para una novela visual: guía paso a paso',
    description: '¿Sueñas con crear tu propia novela visual? Esta guía te lleva a través de todo el proceso — desde la idea hasta el guión terminado.',
    seoTitle: `Cómo escribir un guión para una novela visual: guía paso a paso — ${BRAND}`,
    seoDescription: '¿Sueñas con crear tu propia novela visual? Esta guía te lleva a través de todo el proceso — desde la idea hasta el guión terminado.',
    content: (
      <>
        <p>¿Sueñas con crear tu propia novela visual? Esta guía te lleva a través de todo el proceso — desde la idea hasta el guión terminado. Y si quieres ver tu historia en acción de inmediato, la plataforma <a href={localePath('/')}>{BRAND}</a> te permite crear novelas visuales sin programación.</p>

        <h2>¿Qué es un guión de novela visual?</h2>

        <p>Un guión de novela visual difiere de un guión de película o libro:</p>

        <ul>
          <li><strong>Estructura impulsada por diálogos</strong> — la mayor parte del texto es diálogo de personajes</li>
          <li><strong>Ramificación</strong> — la historia se divide según las elecciones del jugador</li>
          <li><strong>Múltiples finales</strong> — diferentes conclusiones para caminos diferentes</li>
          <li><strong>Notas técnicas</strong> — instrucciones para artistas, programadores, compositores</li>
        </ul>

        <h2>Etapa 1: Concepto</h2>

        <h3>Define el género</h3>

        <p>Elige el género principal de tu novela:</p>

        <ul>
          <li><strong>Romance</strong> — una historia de amor con desarrollo de relaciones</li>
          <li><strong>Detective</strong> — investigación con misterios</li>
          <li><strong>Horror</strong> — atmósfera aterradora y tensión</li>
          <li><strong>Fantasía</strong> — mundos mágicos y aventuras</li>
          <li><strong>Slice of Life</strong> — vida cotidiana y amistad</li>
          <li><strong>Ciencia ficción</strong> — tecnología y el futuro</li>
        </ul>

        <h3>Crea una sinopsis</h3>

        <p>Una sinopsis es la esencia de tu historia en 1–2 oraciones:</p>

        <blockquote><em>Un estudiante de academia mágica descubre una conspiración entre maestros y debe elegir: exponerlos y arriesgar la vida de sus amigos, o guardar silencio.</em></blockquote>

        <blockquote><em>Una chica se muda a una ciudad nueva y conoce a tres chicos — cada uno oculta un secreto conectado a su pasado.</em></blockquote>

        <h3>Determina el tono y la atmósfera</h3>

        <ul>
          <li>¿Serio o cómico?</li>
          <li>¿Luminoso u oscuro?</li>
          <li>¿Realista o estilizado?</li>
        </ul>

        <h2>Etapa 2: Mundo y escenario</h2>

        <h3>Crea el escenario</h3>

        <p><strong>Preguntas para desarrollar:</strong></p>
        <ul>
          <li>¿Dónde ocurre la historia? (país, ciudad, era)</li>
          <li>¿Cuáles son las características de este mundo? (magia, tecnología, sociedad)</li>
          <li>¿Qué ubicaciones visitará el jugador?</li>
        </ul>

        <p><strong>Ejemplos de ubicaciones para una historia escolar:</strong></p>
        <ul>
          <li>Aula</li>
          <li>Azotea de la escuela</li>
          <li>Sala de club</li>
          <li>Casa del personaje principal</li>
          <li>Café en la ciudad</li>
        </ul>

        <h3>Establece las reglas del mundo</h3>

        <p>Si tu historia tiene elementos fantásticos, define sus límites:</p>
        <ul>
          <li>¿Cómo funciona la magia?</li>
          <li>¿Cuáles son las limitaciones?</li>
          <li>¿Qué es posible y qué no?</li>
        </ul>

        <h2>Etapa 3: Personajes</h2>

        <h3>El protagonista</h3>

        <p>En novelas visuales, el jugador se identifica con el protagonista:</p>

        <p><strong>Desarrolla:</strong></p>
        <ul>
          <li>Nombre y edad</li>
          <li>Apariencia (si se muestra)</li>
          <li>Personalidad y rasgos</li>
          <li>Motivación — qué quieren lograr</li>
          <li>Defectos — qué los hace humanos</li>
          <li>Arco de carácter — cómo cambian al final</li>
        </ul>

        <h3>Personajes clave</h3>

        <p>Para cada personaje importante, crea un perfil:</p>

        <pre><code>Nombre: Alice Morozova
Edad: 17
Rol: Interés romántico / ayudante
Apariencia: Cabello negro hasta los hombros, ojos verdes
Personalidad: Reservada, inteligente, misteriosa
Secreto: Conoce la verdad sobre la muerte de los padres del protagonista
Relación con el protagonista: Compañera de clase, mantiene distancia</code></pre>

        <h3>Tipos de personajes</h3>

        <ul>
          <li><strong>Protagonista</strong> — el personaje principal (a menudo sin voz)</li>
          <li><strong>Intereses románticos</strong> — parejas potenciales</li>
          <li><strong>Aliados</strong> — amigos y ayudantes</li>
          <li><strong>Antagonistas</strong> — enemigos y obstáculos</li>
          <li><strong>Secundarios</strong> — maestros, familia, personajes de fondo</li>
        </ul>

        <h2>Etapa 4: Estructura de la trama</h2>

        <h3>Estructura clásica de tres actos</h3>

        <p><strong>Acto 1: Exposición (20–25%)</strong></p>
        <ul>
          <li>Presenta al personaje y al mundo</li>
          <li>Un incidente que desencadena la trama</li>
          <li>El protagonista acepta el &quot;llamado&quot;</li>
        </ul>

        <p><strong>Acto 2: Desarrollo (50–60%)</strong></p>
        <ul>
          <li>El protagonista enfrenta obstáculos</li>
          <li>Las relaciones con los personajes se desarrollan</li>
          <li>Elecciones clave del jugador</li>
          <li>Punto de no retorno</li>
        </ul>

        <p><strong>Acto 3: Clímax y resolución (20–25%)</strong></p>
        <ul>
          <li>Confrontación final</li>
          <li>Secretos revelados</li>
          <li>Final (depende de las elecciones)</li>
        </ul>

        <h3>Sistema de ramificación</h3>

        <p><strong>Estructura lineal con ramas:</strong></p>
        <pre><code>Inicio {'→'} Opción 1 {'→'} Evento A {'→'} Opción 2 {'→'} Final 1/2/3
        {'→'} Evento B {'→'} Opción 3 {'→'} Final 4/5</code></pre>

        <p><strong>Sistema de rutas:</strong></p>
        <pre><code>Prólogo común {'→'} Elige personaje {'→'} Ruta de Alice {'→'} Finales de Alice
                           {'→'} Ruta de David {'→'} Finales de David
                           {'→'} Ruta de Elena {'→'} Finales de Elena</code></pre>

        <h3>Tipos de elecciones</h3>

        <ul>
          <li><strong>Trama</strong> — afectan el desarrollo de la historia</li>
          <li><strong>Carácter</strong> — definen la personalidad del héroe</li>
          <li><strong>Romántica</strong> — desarrollan relaciones con personajes</li>
          <li><strong>Moral</strong> — prueban los valores del jugador</li>
          <li><strong>Cosmética</strong> — no afectan la trama, pero añaden inmersión</li>
        </ul>

        <h2>Etapa 5: Escritura del guión</h2>

        <h3>Formato de guión de novela visual</h3>

        <pre><code>[Ubicación: Aula escolar, día]
[Música: school_calm.ogg]

Alice está de pie junto a la ventana, mirando el patio de otoño.

ALICE (pensativa)
&quot;A veces pienso que todo esto es solo un sueño.&quot;

[El sprite de Alice cambia a triste]

{'>'} Opción:
1. &quot;¿Qué quieres decir?&quot; {'→'} goto scene_question
2. &quot;Entiendo lo que quieres decir.&quot; {'→'} goto scene_understand
3. [Abrázala] {'→'} goto scene_hug (requiere: affection {'>'}= 5)

=== scene_question ===
PROTAGONISTA
&quot;¿De qué hablas?&quot;

ALICE (sorprendida)
&quot;De nada. Olvídalo.&quot;

[Alice se da la vuelta]</code></pre>

        <h3>Consejos para escribir diálogos</h3>

        <p><strong>Haz que el diálogo sea natural:</strong></p>
        <ul>
          <li>❌ &quot;Estoy experimentando tristeza debido al evento ocurrido&quot;</li>
          <li>✅ &quot;Yo... estoy mal. No puedo dejar de pensar en esto.&quot;</li>
        </ul>

        <p><strong>Muestra el carácter a través del habla:</strong></p>
        <ul>
          <li>El personaje rudo habla en frases cortas</li>
          <li>El intelectual usa palabras complejas</li>
          <li>La persona tímida a menudo tartamudea</li>
        </ul>

        <p><strong>Evita las &quot;cabezas parlantes&quot;:</strong></p>
        <ul>
          <li>Añade acciones entre líneas</li>
          <li>Describe reacciones de personajes</li>
          <li>Usa el entorno</li>
        </ul>

        <h3>Notas técnicas</h3>

        <p>Añade a tu guión para el equipo:</p>

        <pre><code>[CG: first_kiss.png] — ilustración especial
[SFX: door_slam.wav] — efecto de sonido
[La pantalla se sacude] — efecto visual
[Variable: trust_alice +1] — cambiar estadísticas</code></pre>

        <h2>Etapa 6: Finales</h2>

        <h3>Tipos de finales</h3>

        <ul>
          <li><strong>True End</strong> — el final &quot;verdadero&quot; que revela toda la historia</li>
          <li><strong>Good End</strong> — conclusión feliz para una ruta específica</li>
          <li><strong>Normal End</strong> — final neutral</li>
          <li><strong>Bad End</strong> — resultado negativo</li>
          <li><strong>Secret End</strong> — final oculto para condiciones especiales</li>
        </ul>

        <h3>Cómo hacer finales satisfactorios</h3>

        <ol>
          <li><strong>Cada final debe tener sentido</strong> — no castigues al jugador por elecciones &quot;incorrectas&quot;</li>
          <li><strong>Cierra tramas</strong> — responde las preguntas que planteaste</li>
          <li><strong>Resonancia emocional</strong> — el final debe evocar emociones</li>
          <li><strong>Recompensa la exploración</strong> — los buenos finales requieren esfuerzo</li>
        </ol>

        <h2>Crear novelas en {BRAND}</h2>

        <p>Si no quieres tratar con programación y motores de juego, <a href={localePath('/')}>{BRAND}</a> ofrece un enfoque alternativo:</p>

        <h3>Cómo funciona</h3>

        <ol>
          <li><strong>Crea un escenario</strong> — describe el mundo de tu novela</li>
          <li><strong>Configura personajes</strong> — sus personalidades y roles</li>
          <li><strong>Establece reglas</strong> — qué puede suceder en la historia</li>
          <li><strong>La IA la anima</strong> — la inteligencia artificial impulsará la narrativa</li>
        </ol>

        <h3>Ventajas de la plataforma</h3>

        <ul>
          <li><strong>Sin programación</strong> — no es necesario conocer Ren&apos;Py o Unity</li>
          <li><strong>Resultados instantáneos</strong> — tu historia funciona inmediatamente</li>
          <li><strong>Contenido infinito</strong> — la IA genera nuevos eventos</li>
          <li><strong>Prueba ideas</strong> — valida tu concepto antes del desarrollo completo</li>
        </ul>

        <h3>Para quién es</h3>

        <ul>
          <li>Autores aspirantes que quieren probar sus ideas</li>
          <li>Escritores que quieren prototipificar un escenario</li>
          <li>Cualquiera que quiera hacer una novela sin un equipo</li>
        </ul>

        <h2>Herramientas para desarrollo tradicional</h2>

        <h3>Motores de novelas visuales</h3>

        <p><strong>Ren&apos;Py</strong> (gratuito)</p>
        <ul>
          <li>Motor de novela visual más popular</li>
          <li>Lenguaje de secuencias de comandos similar a Python</li>
          <li>Gran comunidad</li>
        </ul>

        <p><strong>TyranoBuilder</strong> (de pago)</p>
        <ul>
          <li>Editor visual</li>
          <li>Sin programación requerida</li>
          <li>Exportar a múltiples plataformas</li>
        </ul>

        <p><strong>Visual Novel Maker</strong> (de pago)</p>
        <ul>
          <li>Editor de RPG Maker</li>
          <li>Interfaz simple</li>
          <li>Buena documentación</li>
        </ul>

        <h3>Software de escritura</h3>

        <ul>
          <li><strong>Twine</strong> — planificación visual de ramas</li>
          <li><strong>Scrivener</strong> — organiza textos grandes</li>
          <li><strong>Google Docs</strong> — escritura colaborativa</li>
        </ul>

        <h2>Lista de verificación antes de comenzar</h2>

        <ul>
          <li>Género y tono definidos</li>
          <li>Sinopsis escrita</li>
          <li>Escenario desarrollado</li>
          <li>Perfiles de personajes creados</li>
          <li>Estructura de trama esbozada</li>
          <li>Sistema de ramificación planificado</li>
          <li>Tipos de finales determinados</li>
          <li>Herramienta de desarrollo elegida</li>
        </ul>

        <h2>Conclusión</h2>

        <p>Escribir un guión para una novela visual es un proceso creativo emocionante. De la idea a la historia terminada puede llevar de semanas a años según el alcance del proyecto.</p>

        <p>Si apenas estás comenzando, intenta crear una novela corta para 1–2 horas de juego. O usa <a href={localePath('/')}>{BRAND}</a> para ver tus ideas cobrar vida de inmediato — sin programación y sin meses de desarrollo.</p>

        <p><strong>Tu historia espera ser contada.</strong></p>
      </>
    ),
  },

  italian: {
    title: '✍️ Come scrivere una sceneggiatura per un romanzo visivo: guida passo dopo passo',
    description: "Sogni di creare il tuo romanzo visivo? Questa guida ti accompagna attraverso l'intero processo — dall'idea alla sceneggiatura finita.",
    seoTitle: `Come scrivere una sceneggiatura per un romanzo visivo: guida passo dopo passo — ${BRAND}`,
    seoDescription: "Sogni di creare il tuo romanzo visivo? Questa guida ti accompagna attraverso l'intero processo — dall'idea alla sceneggiatura finita.",
    content: (
      <>
        <p>Sogni di creare il tuo romanzo visivo? Questa guida ti accompagna attraverso l&apos;intero processo — dall&apos;idea alla sceneggiatura finita. E se vuoi vedere la tua storia in azione subito, la piattaforma <a href={localePath('/')}>{BRAND}</a> ti consente di creare romanzi visivi senza programmazione.</p>

        <h2>Che cos&apos;è una sceneggiatura di romanzo visivo?</h2>

        <p>Una sceneggiatura per VN differisce da una sceneggiatura di film o libro:</p>

        <ul>
          <li><strong>Struttura basata sul dialogo</strong> — la maggior parte del testo è il dialogo dei personaggi</li>
          <li><strong>Ramificazione</strong> — la storia si divide in base alle scelte del giocatore</li>
          <li><strong>Finali multipli</strong> — conclusioni diverse per percorsi diversi</li>
          <li><strong>Note tecniche</strong> — istruzioni per artisti, programmatori, compositori</li>
        </ul>

        <h2>Fase 1: Concetto</h2>

        <h3>Definisci il genere</h3>

        <p>Scegli il genere principale del tuo romanzo:</p>

        <ul>
          <li><strong>Romanticismo</strong> — una storia d&apos;amore con sviluppo delle relazioni</li>
          <li><strong>Detective</strong> — indagine con misteri</li>
          <li><strong>Horror</strong> — atmosfera spaventosa e tensione</li>
          <li><strong>Fantasy</strong> — mondi magici e avventure</li>
          <li><strong>Slice of Life</strong> — vita quotidiana e amicizia</li>
          <li><strong>Fantascienza</strong> — tecnologia e futuro</li>
        </ul>

        <h3>Crea una logline</h3>

        <p>Una logline è l&apos;essenza della tua storia in 1–2 frasi:</p>

        <blockquote><em>Una studentessa di un&apos;accademia magica scopre una cospirazione tra i professori e deve scegliere: esporla e rischiare la vita dei suoi amici, o stare in silenzio.</em></blockquote>

        <blockquote><em>Una ragazza si trasferisce in una nuova città e incontra tre ragazzi — ognuno nasconde un segreto legato al suo passato.</em></blockquote>

        <h3>Determina il tono e l&apos;atmosfera</h3>

        <ul>
          <li>Serio o comico?</li>
          <li>Luminoso o scuro?</li>
          <li>Realistico o stilizzato?</li>
        </ul>

        <h2>Fase 2: Mondo e ambientazione</h2>

        <h3>Crea l&apos;ambientazione</h3>

        <p><strong>Domande da sviluppare:</strong></p>
        <ul>
          <li>Dove si svolge la storia? (paese, città, epoca)</li>
          <li>Quali sono le caratteristiche di questo mondo? (magia, tecnologia, società)</li>
          <li>Quali luoghi visiterà il giocatore?</li>
        </ul>

        <p><strong>Esempi di luoghi per una storia scolastica:</strong></p>
        <ul>
          <li>Aula</li>
          <li>Tetto della scuola</li>
          <li>Sala del club</li>
          <li>Casa del personaggio principale</li>
          <li>Caffè in città</li>
        </ul>

        <h3>Stabilisci le regole del mondo</h3>

        <p>Se la tua storia ha elementi fantastici, definisci i loro limiti:</p>
        <ul>
          <li>Come funziona la magia?</li>
          <li>Quali sono i vincoli?</li>
          <li>Cosa è possibile e cosa no?</li>
        </ul>

        <h2>Fase 3: Personaggi</h2>

        <h3>Il protagonista</h3>

        <p>Nei romanzi visivi, il giocatore si identifica con il protagonista:</p>

        <p><strong>Sviluppa:</strong></p>
        <ul>
          <li>Nome ed età</li>
          <li>Apparenza (se mostrata)</li>
          <li>Personalità e tratti</li>
          <li>Motivazione — cosa vogliono realizzare</li>
          <li>Difetti — cosa li rende umani</li>
          <li>Arco dei personaggi — come cambiano entro la fine</li>
        </ul>

        <h3>Personaggi chiave</h3>

        <p>Per ogni personaggio importante, crea un profilo:</p>

        <pre><code>Nome: Alice Morozova
Età: 17
Ruolo: Interesse romantico / aiutante
Apparenza: Capelli neri alle spalle, occhi verdi
Personalità: Riservata, intelligente, misteriosa
Segreto: Conosce la verità sulla morte dei genitori del protagonista
Relazione con il protagonista: Compagna di classe, mantiene distanza</code></pre>

        <h3>Tipi di personaggi</h3>

        <ul>
          <li><strong>Protagonista</strong> — il personaggio principale (spesso silenzioso)</li>
          <li><strong>Interessi romantici</strong> — partner potenziali</li>
          <li><strong>Alleati</strong> — amici e aiutanti</li>
          <li><strong>Antagonisti</strong> — nemici e ostacoli</li>
          <li><strong>Secondari</strong> — insegnanti, famiglia, personaggi di sfondo</li>
        </ul>

        <h2>Fase 4: Struttura della trama</h2>

        <h3>Struttura classica in tre atti</h3>

        <p><strong>Atto 1: Esposizione (20–25%)</strong></p>
        <ul>
          <li>Presenta il personaggio e il mondo</li>
          <li>Un incidente che scatena la trama</li>
          <li>Il protagonista accetta la &quot;chiamata&quot;</li>
        </ul>

        <p><strong>Atto 2: Sviluppo (50–60%)</strong></p>
        <ul>
          <li>Il protagonista affronta ostacoli</li>
          <li>Le relazioni con i personaggi si sviluppano</li>
          <li>Scelte chiave del giocatore</li>
          <li>Punto di non ritorno</li>
        </ul>

        <p><strong>Atto 3: Climax e risoluzione (20–25%)</strong></p>
        <ul>
          <li>Scontro finale</li>
          <li>Segreti rivelati</li>
          <li>Fine (dipende dalle scelte)</li>
        </ul>

        <h3>Sistema di ramificazione</h3>

        <p><strong>Struttura lineare con rami:</strong></p>
        <pre><code>Inizio {'→'} Scelta 1 {'→'} Evento A {'→'} Scelta 2 {'→'} Fine 1/2/3
        {'→'} Evento B {'→'} Scelta 3 {'→'} Fine 4/5</code></pre>

        <p><strong>Sistema di percorsi:</strong></p>
        <pre><code>Prologo comune {'→'} Scegli personaggio {'→'} Percorso di Alice {'→'} Finali di Alice
                           {'→'} Percorso di David {'→'} Finali di David
                           {'→'} Percorso di Elena {'→'} Finali di Elena</code></pre>

        <h3>Tipi di scelte</h3>

        <ul>
          <li><strong>Trama</strong> — influenzano lo sviluppo della storia</li>
          <li><strong>Carattere</strong> — definiscono la personalità dell&apos;eroe</li>
          <li><strong>Romantico</strong> — sviluppano relazioni con i personaggi</li>
          <li><strong>Morale</strong> — testano i valori del giocatore</li>
          <li><strong>Cosmetico</strong> — non influenzano la trama, ma aggiungono immersione</li>
        </ul>

        <h2>Fase 5: Scrittura della sceneggiatura</h2>

        <h3>Formato della sceneggiatura VN</h3>

        <pre><code>[Luogo: Aula scolastica, giorno]
[Musica: school_calm.ogg]

Alice è in piedi vicino alla finestra, guardando il cortile autunnale.

ALICE (pensierosa)
&quot;A volte penso che tutto questo sia solo un sogno.&quot;

[Lo sprite di Alice cambia in triste]

{'>'} Scelta:
1. &quot;Cosa intendi?&quot; {'→'} goto scene_question
2. &quot;Capisco quello che intendi.&quot; {'→'} goto scene_understand
3. [Abbracciarla] {'→'} goto scene_hug (richiede: affection {'>'}= 5)

=== scene_question ===
PROTAGONISTA
&quot;Di cosa stai parlando?&quot;

ALICE (sorpresa)
&quot;Di niente. Dimenticalo.&quot;

[Alice si allontana]</code></pre>

        <h3>Suggerimenti per scrivere i dialoghi</h3>

        <p><strong>Rendi il dialogo naturale:</strong></p>
        <ul>
          <li>❌ &quot;Sto provando tristezza a causa dell&apos;evento accaduto&quot;</li>
          <li>✅ &quot;Io... sto male. Non riesco a smettere di pensarci.&quot;</li>
        </ul>

        <p><strong>Mostra il carattere attraverso il parlato:</strong></p>
        <ul>
          <li>Il personaggio rude parla in frasi brevi</li>
          <li>L&apos;intellettuale usa parole complesse</li>
          <li>La persona timida spesso balbetta</li>
        </ul>

        <p><strong>Evita le &quot;teste che parlano&quot;:</strong></p>
        <ul>
          <li>Aggiungi azioni tra le battute</li>
          <li>Descrivi le reazioni dei personaggi</li>
          <li>Usa l&apos;ambiente</li>
        </ul>

        <h3>Note tecniche</h3>

        <p>Aggiungi alla tua sceneggiatura per il team:</p>

        <pre><code>[CG: first_kiss.png] — illustrazione speciale
[SFX: door_slam.wav] — effetto sonoro
[Lo schermo trema] — effetto visivo
[Variabile: trust_alice +1] — cambia statistiche</code></pre>

        <h2>Fase 6: Finali</h2>

        <h3>Tipi di finali</h3>

        <ul>
          <li><strong>True End</strong> — il finale &quot;vero&quot; che rivela l&apos;intera storia</li>
          <li><strong>Good End</strong> — conclusione felice per un percorso specifico</li>
          <li><strong>Normal End</strong> — finale neutro</li>
          <li><strong>Bad End</strong> — esito negativo</li>
          <li><strong>Secret End</strong> — finale nascosto per condizioni speciali</li>
        </ul>

        <h3>Come fare finali soddisfacenti</h3>

        <ol>
          <li><strong>Ogni finale dovrebbe avere senso</strong> — non punire il giocatore per le scelte &quot;sbagliate&quot;</li>
          <li><strong>Chiudi le linee della trama</strong> — rispondi alle domande che hai posto</li>
          <li><strong>Risonanza emotiva</strong> — il finale dovrebbe suscitare emozioni</li>
          <li><strong>Premia l&apos;esplorazione</strong> — i buoni finali richiedono sforzo</li>
        </ol>

        <h2>Creare romanzi su {BRAND}</h2>

        <p>Se non vuoi affrontare la programmazione e i motori di gioco, <a href={localePath('/')}>{BRAND}</a> offre un approccio alternativo:</p>

        <h3>Come funziona</h3>

        <ol>
          <li><strong>Crea un&apos;ambientazione</strong> — descrivi il mondo del tuo romanzo</li>
          <li><strong>Configura i personaggi</strong> — le loro personalità e ruoli</li>
          <li><strong>Stabilisci le regole</strong> — cosa può accadere nella storia</li>
          <li><strong>L&apos;IA la anima</strong> — l&apos;intelligenza artificiale guiderà la narrazione</li>
        </ol>

        <h3>Vantaggi della piattaforma</h3>

        <ul>
          <li><strong>Nessuna programmazione</strong> — non è necessario conoscere Ren&apos;Py o Unity</li>
          <li><strong>Risultati istantanei</strong> — la tua storia funziona immediatamente</li>
          <li><strong>Contenuto infinito</strong> — l&apos;IA genera nuovi eventi</li>
          <li><strong>Testa le idee</strong> — convalida il tuo concetto prima dello sviluppo completo</li>
        </ul>

        <h3>Per chi è adatto</h3>

        <ul>
          <li>Autori aspiranti che vogliono testare le loro idee</li>
          <li>Scrittori che vogliono prototipare un&apos;ambientazione</li>
          <li>Chiunque voglia creare un romanzo senza una squadra</li>
        </ul>

        <h2>Strumenti per lo sviluppo tradizionale</h2>

        <h3>Motori di romanzi visivi</h3>

        <p><strong>Ren&apos;Py</strong> (gratuito)</p>
        <ul>
          <li>Motore VN più popolare</li>
          <li>Linguaggio di scripting simile a Python</li>
          <li>Grande comunità</li>
        </ul>

        <p><strong>TyranoBuilder</strong> (a pagamento)</p>
        <ul>
          <li>Editor visivo</li>
          <li>Nessuna programmazione richiesta</li>
          <li>Esporta su più piattaforme</li>
        </ul>

        <p><strong>Visual Novel Maker</strong> (a pagamento)</p>
        <ul>
          <li>Editor di RPG Maker</li>
          <li>Interfaccia semplice</li>
          <li>Buona documentazione</li>
        </ul>

        <h3>Software di scrittura</h3>

        <ul>
          <li><strong>Twine</strong> — pianificazione visiva dei rami</li>
          <li><strong>Scrivener</strong> — organizza grandi testi</li>
          <li><strong>Google Docs</strong> — scrittura collaborativa</li>
        </ul>

        <h2>Checklist prima di iniziare</h2>

        <ul>
          <li>Genere e tono definiti</li>
          <li>Logline scritta</li>
          <li>Ambientazione sviluppata</li>
          <li>Profili dei personaggi creati</li>
          <li>Struttura della trama delineata</li>
          <li>Sistema di ramificazione pianificato</li>
          <li>Tipi di finali determinati</li>
          <li>Strumento di sviluppo scelto</li>
        </ul>

        <h2>Conclusione</h2>

        <p>Scrivere una sceneggiatura per un romanzo visivo è un processo creativo entusiasmante. Dall&apos;idea alla storia finita può richiedere da settimane a anni a seconda dell&apos;ambito del progetto.</p>

        <p>Se stai appena iniziando, prova a creare un breve romanzo per 1–2 ore di gioco. Oppure usa <a href={localePath('/')}>{BRAND}</a> per vedere le tue idee prendere vita subito — senza programmazione e senza mesi di sviluppo.</p>

        <p><strong>La tua storia aspetta di essere raccontata.</strong></p>
      </>
    ),
  },

  polish: {
    title: '✍️ Jak napisać scenariusz dla powieści wizualnej: przewodnik krok po kroku',
    description: 'Marzysz o stworzeniu własnej powieści wizualnej? Ten przewodnik przeprowadzi Cię przez cały proces — od pomysłu do gotowego scenariusza.',
    seoTitle: `Jak napisać scenariusz dla powieści wizualnej: przewodnik krok po kroku — ${BRAND}`,
    seoDescription: 'Marzysz o stworzeniu własnej powieści wizualnej? Ten przewodnik przeprowadzi Cię przez cały proces — od pomysłu do gotowego scenariusza.',
    content: (
      <>
        <p>Marzysz o stworzeniu własnej powieści wizualnej? Ten przewodnik przeprowadzi Cię przez cały proces — od pomysłu do gotowego scenariusza. A jeśli chcesz od razu zobaczyć swoją historię w akcji, platforma <a href={localePath('/')}>{BRAND}</a> pozwala na tworzenie powieści wizualnych bez programowania.</p>

        <h2>Czym jest scenariusz powieści wizualnej?</h2>

        <p>Scenariusz VN różni się od scenariusza filmowego lub książkowego:</p>

        <ul>
          <li><strong>Struktura oparta na dialogach</strong> — większość tekstu to dialogi postaci</li>
          <li><strong>Rozgałęzienie</strong> — historia rozdziela się na podstawie wyborów gracza</li>
          <li><strong>Wiele zakończeń</strong> — różne zakonczenia dla różnych ścieżek</li>
          <li><strong>Notatki techniczne</strong> — instrukcje dla artystów, programistów, kompozytorów</li>
        </ul>

        <h2>Etap 1: Koncepcja</h2>

        <h3>Zdefiniuj gatunek</h3>

        <p>Wybierz główny gatunek swojej powieści:</p>

        <ul>
          <li><strong>Romans</strong> — historia miłosna z rozwojem relacji</li>
          <li><strong>Detektyw</strong> — śledztwo z zagadkami</li>
          <li><strong>Horror</strong> — straszna atmosfera i napięcie</li>
          <li><strong>Fantasy</strong> — magiczne światy i przygody</li>
          <li><strong>Slice of Life</strong> — codzienna kiana i przyjaźń</li>
          <li><strong>Fantastyka naukowa</strong> — technologia i przyszłość</li>
        </ul>

        <h3>Utwórz logline</h3>

        <p>Logline to esencja Twojej historii w 1–2 zdaniach:</p>

        <blockquote><em>Studentka akademii magii odkrywa spisek wśród nauczycieli i musi wybrać: ich wydać i ryzykować życiem przyjaciół, czy milczeć.</em></blockquote>

        <blockquote><em>Dziewczyna przenosi się do nowego miasta i poznaje trzech chłopców — każdy ukrywa sekret związany z jej przeszłością.</em></blockquote>

        <h3>Określ ton i atmosferę</h3>

        <ul>
          <li>Poważny czy komiczny?</li>
          <li>Jasny czy ciemny?</li>
          <li>Realistyczny czy stylizowany?</li>
        </ul>

        <h2>Etap 2: Świat i sceneria</h2>

        <h3>Stwórz scenę</h3>

        <p><strong>Pytania do opracowania:</strong></p>
        <ul>
          <li>Gdzie dzieje się historia? (kraj, miasto, era)</li>
          <li>Jakie są cechy tego świata? (magia, technologia, społeczeństwo)</li>
          <li>Jakie lokacje odwiedzi gracz?</li>
        </ul>

        <p><strong>Przykładowe lokacje dla szkolnej historii:</strong></p>
        <ul>
          <li>Sala lekcyjna</li>
          <li>Dach szkoły</li>
          <li>Sala klubu</li>
          <li>Dom głównej postaci</li>
          <li>Kawiarnia w mieście</li>
        </ul>

        <h3>Ustanów zasady świata</h3>

        <p>Jeśli Twoja historia ma elementy fantastyczne, zdefiniuj ich granice:</p>
        <ul>
          <li>Jak działa magia?</li>
          <li>Jakie są ograniczenia?</li>
          <li>Co jest możliwe, a co nie?</li>
        </ul>

        <h2>Etap 3: Postacie</h2>

        <h3>Główny bohater</h3>

        <p>W powieściach wizualnych gracz utożsamia się z protagonistą:</p>

        <p><strong>Opracuj:</strong></p>
        <ul>
          <li>Imię i wiek</li>
          <li>Wygląd (jeśli pokazany)</li>
          <li>Osobowość i cechy</li>
          <li>Motywacja — co chce osiągnąć</li>
          <li>Wady — co czyni go człowiekiem</li>
          <li>Łuk postaci — jak zmienia się do końca</li>
        </ul>

        <h3>Kluczowe postacie</h3>

        <p>Dla każdej ważnej postaci utwórz profil:</p>

        <pre><code>Imię: Alice Morozova
Wiek: 17
Rola: Interes romantyczny / pomocnik
Wygląd: Czarne włosy do ramion, zielone oczy
Osobowość: Wstrzymana, inteligentna, tajemnicza
Sekret: Zna prawdę o śmierci rodziców protagonisty
Relacja z protagonistą: Koleżanka z klasy, utrzymuje dystans</code></pre>

        <h3>Typy postaci</h3>

        <ul>
          <li><strong>Protagonista</strong> — główna postać (często bez głosu)</li>
          <li><strong>Interesy romantyczne</strong> — potencjalni partnerzy</li>
          <li><strong>Sojusznicy</strong> — przyjaciele i pomocnicy</li>
          <li><strong>Antagoniści</strong> — wrogowie i przeszkody</li>
          <li><strong>Drugorzędne</strong> — nauczyciele, rodzina, postaci drugoplanowe</li>
        </ul>

        <h2>Etap 4: Struktura fabuły</h2>

        <h3>Klasyczna struktura trzech aktów</h3>

        <p><strong>Akt 1: Ekspozycja (20–25%)</strong></p>
        <ul>
          <li>Przedstaw postać i świat</li>
          <li>Incydent, który uruchamia fabułę</li>
          <li>Protagonist akceptuje &quot;wezwanie&quot;</li>
        </ul>

        <p><strong>Akt 2: Rozwój (50–60%)</strong></p>
        <ul>
          <li>Protagonist stoi w obliczu przeszkód</li>
          <li>Relacje z postaciami się rozwijają</li>
          <li>Kluczowe wybory gracza</li>
          <li>Punkt bez powrotu</li>
        </ul>

        <p><strong>Akt 3: Punkt kulminacyjny i rozwiązanie (20–25%)</strong></p>
        <ul>
          <li>Finalna konfrontacja</li>
          <li>Ujawnione sekrety</li>
          <li>Zakończenie (zależy od wyborów)</li>
        </ul>

        <h3>System rozgałęzień</h3>

        <p><strong>Struktura liniowa z rozgałęzieniami:</strong></p>
        <pre><code>Start {'→'} Wybór 1 {'→'} Zdarzenie A {'→'} Wybór 2 {'→'} Zakończenie 1/2/3
      {'→'} Zdarzenie B {'→'} Wybór 3 {'→'} Zakończenie 4/5</code></pre>

        <p><strong>System tras:</strong></p>
        <pre><code>Wspólny prolog {'→'} Wybierz postać {'→'} Trasa Alice {'→'} Zakończenia Alice
                           {'→'} Trasa Davida {'→'} Zakończenia Davida
                           {'→'} Trasa Eleny {'→'} Zakończenia Eleny</code></pre>

        <h3>Typy wyborów</h3>

        <ul>
          <li><strong>Fabułę</strong> — wpływają na rozwój historii</li>
          <li><strong>Charakter</strong> — definiują osobowość bohatera</li>
          <li><strong>Romantyczne</strong> — rozwijają relacje z postaciami</li>
          <li><strong>Moralne</strong> — testują wartości gracza</li>
          <li><strong>Kosmetyczne</strong> — nie wpływają na fabułę, ale dodają immersji</li>
        </ul>

        <h2>Etap 5: Pisanie scenariusza</h2>

        <h3>Format scenariusza VN</h3>

        <pre><code>[Lokacja: Sala lekcyjna, dzień]
[Muzyka: school_calm.ogg]

Alice stoi przy oknie, patrząc na jesienny dziedziniec.

ALICE (zamyślnie)
&quot;Czasami myślę, że to wszystko jest tylko snem.&quot;

[Sprite Alice zmienia się na smutny]

{'>'} Wybór:
1. &quot;Co masz na myśli?&quot; {'→'} goto scene_question
2. &quot;Rozumiem, co masz na myśli.&quot; {'→'} goto scene_understand
3. [Objąć ją] {'→'} goto scene_hug (wymaga: affection {'>'}= 5)

=== scene_question ===
PROTAGONISTA
&quot;O czym mówisz?&quot;

ALICE (zaskoczona)
&quot;O nic. Zapomnij.&quot;

[Alice odwraca się]</code></pre>

        <h3>Wskazówki do pisania dialogów</h3>

        <p><strong>Spraw, aby dialog był naturalny:</strong></p>
        <ul>
          <li>❌ &quot;Doświadczam smutku z powodu zdarzenia, które się wydarzyło&quot;</li>
          <li>✅ &quot;Ja... czuję się źle. Nie mogę przestać o tym myśleć.&quot;</li>
        </ul>

        <p><strong>Pokaż charakter poprzez mowę:</strong></p>
        <ul>
          <li>Szorstka postać mówi krótkimi frazami</li>
          <li>Intelektualista używa złożonych słów</li>
          <li>Nieśmiała osoba często się jąka</li>
        </ul>

        <p><strong>Unikaj &quot;mówiących głów&quot;:</strong></p>
        <ul>
          <li>Dodaj działania między replikami</li>
          <li>Opisz reakcje postaci</li>
          <li>Używaj otoczenia</li>
        </ul>

        <h3>Notatki techniczne</h3>

        <p>Dodaj do swojego scenariusza dla zespołu:</p>

        <pre><code>[CG: first_kiss.png] — specjalna ilustracja
[SFX: door_slam.wav] — efekt dźwiękowy
[Ekran trzęsie się] — efekt wizualny
[Zmienna: trust_alice +1] — zmień statystyki</code></pre>

        <h2>Etap 6: Zakończenia</h2>

        <h3>Typy zakończeń</h3>

        <ul>
          <li><strong>True End</strong> — &quot;prawdziwe&quot; zakończenie, które ujawnia całą historię</li>
          <li><strong>Good End</strong> — szczęśliwe zakończenie dla konkretnej ścieżki</li>
          <li><strong>Normal End</strong> — neutralne zakończenie</li>
          <li><strong>Bad End</strong> — negatywny wynik</li>
          <li><strong>Secret End</strong> — ukryte zakończenie dla specjalnych warunków</li>
        </ul>

        <h3>Jak stworzyć satysfakcjonujące zakończenia</h3>

        <ol>
          <li><strong>Każde zakończenie powinno mieć sens</strong> — nie karź gracza za &quot;błędne&quot; wybory</li>
          <li><strong>Zamknij linie fabularne</strong> — odpowiedz na pytania, które postawiłeś</li>
          <li><strong>Rezonans emocjonalny</strong> — zakończenie powinno wzbudzać emocje</li>
          <li><strong>Nagroda za eksplorację</strong> — dobre zakończenia wymagają wysiłku</li>
        </ol>

        <h2>Tworzenie powieści na {BRAND}</h2>

        <p>Jeśli nie chcesz zajmować się programowaniem i silnikami gier, <a href={localePath('/')}>{BRAND}</a> oferuje alternatywne podejście:</p>

        <h3>Jak to działa</h3>

        <ol>
          <li><strong>Stwórz scenę</strong> — opisz świat swojej powieści</li>
          <li><strong>Skonfiguruj postacie</strong> — ich osobowości i role</li>
          <li><strong>Ustanów zasady</strong> — co może się zdarzyć w historii</li>
          <li><strong>Sztuczna inteligencja ożywi to</strong> — sztuczna inteligencja będzie prowadzić narrację</li>
        </ol>

        <h3>Zalety platformy</h3>

        <ul>
          <li><strong>Bez programowania</strong> — nie musisz znać Ren&apos;Py lub Unity</li>
          <li><strong>Natychmiastowe rezultaty</strong> — Twoja historia działa od razu</li>
          <li><strong>Nieskończona zawartość</strong> — sztuczna inteligencja generuje nowe zdarzenia</li>
          <li><strong>Testuj pomysły</strong> — zweryfikuj swoją koncepcję przed pełnym opracowaniem</li>
        </ul>

        <h3>Dla kogo to jest</h3>

        <ul>
          <li>Początkujących autorów, którzy chcą przetestować swoje pomysły</li>
          <li>Pisarze chcący prototypować scenerię</li>
          <li>Każdy, kto chce stworzyć powieść bez zespołu</li>
        </ul>

        <h2>Narzędzia do tradycyjnego rozwoju</h2>

        <h3>Silniki powieści wizualnych</h3>

        <p><strong>Ren&apos;Py</strong> (darmowy)</p>
        <ul>
          <li>Najpopularniejszy silnik VN</li>
          <li>Język skryptowy podobny do Pythona</li>
          <li>Duża społeczność</li>
        </ul>

        <p><strong>TyranoBuilder</strong> (płatny)</p>
        <ul>
          <li>Edytor wizualny</li>
          <li>Nie wymaga programowania</li>
          <li>Export na wiele platform</li>
        </ul>

        <p><strong>Visual Novel Maker</strong> (płatny)</p>
        <ul>
          <li>Edytor od RPG Makera</li>
          <li>Prosty interfejs</li>
          <li>Dobra dokumentacja</li>
        </ul>

        <h3>Oprogramowanie do pisania</h3>

        <ul>
          <li><strong>Twine</strong> — wizualne planowanie gałęzi</li>
          <li><strong>Scrivener</strong> — organizuj duże teksty</li>
          <li><strong>Google Docs</strong> — pisanie wspólpracy</li>
        </ul>

        <h2>Lista kontrolna przed rozpoczęciem</h2>

        <ul>
          <li>Gatunek i ton zdefiniowane</li>
          <li>Logline napisane</li>
          <li>Sceneria opracowana</li>
          <li>Profile postaci stworzone</li>
          <li>Struktura fabuły zarysowana</li>
          <li>System rozgałęzień zaplanowany</li>
          <li>Typy zakończeń określone</li>
          <li>Narzędzie do opracowania wybrane</li>
        </ul>

        <h2>Podsumowanie</h2>

        <p>Pisanie scenariusza dla powieści wizualnej jest ekscytującym procesem twórczym. Od pomysłu do gotowej historii może minąć od kilku tygodni do kilka lat w zależności od zakresu projektu.</p>

        <p>Jeśli dopiero zaczynasz, spróbuj stworzyć krótką powieść na 1–2 godziny gry. Lub użyj <a href={localePath('/')}>{BRAND}</a>, aby zobaczyć swoje pomysły w akcji od razu — bez programowania i bez miesięcy opracowywania.</p>

        <p><strong>Twoja historia czeka, aby być opowiedziana.</strong></p>
      </>
    ),
  },

  portuguese: {
    title: '✍️ Como escrever o roteiro de uma visual novel: guia passo a passo',
    description: 'Sonha em criar sua própria visual novel? Este guia mostra o processo inteiro — da ideia ao roteiro pronto.',
    seoTitle: `Como escrever o roteiro de uma visual novel: guia passo a passo — ${BRAND}`,
    seoDescription: 'Sonha em criar sua própria visual novel? Este guia mostra o processo inteiro — da ideia ao roteiro pronto.',
    content: (
      <>
        <p>Sonha em criar sua própria visual novel? Este guia mostra o processo inteiro — da ideia ao roteiro pronto. E, se você quiser ver sua história funcionando na hora, a plataforma <a href={localePath('/')}>{BRAND}</a> permite criar visual novels sem programar.</p>

        <h2>O que é o roteiro de uma visual novel?</h2>

        <p>O roteiro de uma VN é diferente do roteiro de um filme ou de um livro:</p>

        <ul>
          <li><strong>Estrutura baseada em diálogos</strong> — a maior parte do texto são falas dos personagens</li>
          <li><strong>Ramificações</strong> — a história se divide de acordo com as escolhas do jogador</li>
          <li><strong>Vários finais</strong> — conclusões diferentes para caminhos diferentes</li>
          <li><strong>Notas técnicas</strong> — instruções para artistas, programadores e compositores</li>
        </ul>

        <h2>Etapa 1: o conceito</h2>

        <h3>Defina o gênero</h3>

        <p>Escolha o gênero principal da sua novel:</p>

        <ul>
          <li><strong>Romance</strong> — uma história de amor com o desenvolvimento de um relacionamento</li>
          <li><strong>Detetive</strong> — uma investigação cheia de mistérios</li>
          <li><strong>Terror</strong> — atmosfera assustadora e tensão</li>
          <li><strong>Fantasia</strong> — mundos mágicos e aventuras</li>
          <li><strong>Slice of Life</strong> — o dia a dia e a amizade</li>
          <li><strong>Ficção científica</strong> — tecnologia e futuro</li>
        </ul>

        <h3>Crie uma logline</h3>

        <p>A logline é a essência da sua história em 1–2 frases:</p>

        <blockquote><em>Um aluno de uma academia de magia descobre uma conspiração entre os professores e precisa escolher: denunciá-los e arriscar a vida dos amigos ou ficar calado.</em></blockquote>

        <blockquote><em>Uma garota se muda para uma nova cidade e conhece três rapazes — cada um escondendo um segredo ligado ao passado dela.</em></blockquote>

        <h3>Defina o tom e a atmosfera</h3>

        <ul>
          <li>Séria ou cômica?</li>
          <li>Leve ou sombria?</li>
          <li>Realista ou estilizada?</li>
        </ul>

        <h2>Etapa 2: o mundo e o cenário</h2>

        <h3>Crie o cenário</h3>

        <p><strong>Perguntas para desenvolver:</strong></p>
        <ul>
          <li>Onde a história se passa? (país, cidade, época)</li>
          <li>Quais são as características desse mundo? (magia, tecnologia, sociedade)</li>
          <li>Quais locais o jogador vai visitar?</li>
        </ul>

        <p><strong>Exemplos de locais para uma história escolar:</strong></p>
        <ul>
          <li>Sala de aula</li>
          <li>Terraço da escola</li>
          <li>Sala do clube</li>
          <li>Casa do protagonista</li>
          <li>Café na cidade</li>
        </ul>

        <h3>Estabeleça as regras do mundo</h3>

        <p>Se a sua história tem elementos fantásticos, defina os limites deles:</p>
        <ul>
          <li>Como a magia funciona?</li>
          <li>Quais são as restrições?</li>
          <li>O que é possível e o que não é?</li>
        </ul>

        <h2>Etapa 3: os personagens</h2>

        <h3>O protagonista</h3>

        <p>Nas visual novels, o jogador se identifica com o protagonista:</p>

        <p><strong>Desenvolva:</strong></p>
        <ul>
          <li>Nome e idade</li>
          <li>Aparência (se ele aparecer)</li>
          <li>Personalidade e traços</li>
          <li>Motivação — o que ele quer alcançar</li>
          <li>Defeitos — o que o torna humano</li>
          <li>Arco do personagem — como ele muda até o final</li>
        </ul>

        <h3>Personagens principais</h3>

        <p>Para cada personagem importante, crie uma ficha:</p>

        <pre><code>Nome: Alice Morozova
Idade: 17
Papel: Interesse romântico / ajudante
Aparência: Cabelo preto na altura dos ombros, olhos verdes
Personalidade: Reservada, inteligente, misteriosa
Segredo: Sabe a verdade sobre a morte dos pais do protagonista
Relação com o protagonista: Colega de classe, mantém distância</code></pre>

        <h3>Tipos de personagens</h3>

        <ul>
          <li><strong>Protagonista</strong> — o personagem principal (muitas vezes sem voz)</li>
          <li><strong>Interesses românticos</strong> — possíveis pares</li>
          <li><strong>Aliados</strong> — amigos e ajudantes</li>
          <li><strong>Antagonistas</strong> — inimigos e obstáculos</li>
          <li><strong>Secundários</strong> — professores, família, personagens de fundo</li>
        </ul>

        <h2>Etapa 4: a estrutura do enredo</h2>

        <h3>A estrutura clássica em três atos</h3>

        <p><strong>Ato 1: apresentação (20–25%)</strong></p>
        <ul>
          <li>Apresente o personagem e o mundo</li>
          <li>Um incidente que dá início à trama</li>
          <li>O protagonista aceita o &quot;chamado&quot;</li>
        </ul>

        <p><strong>Ato 2: desenvolvimento (50–60%)</strong></p>
        <ul>
          <li>O protagonista enfrenta obstáculos</li>
          <li>As relações com os personagens se desenvolvem</li>
          <li>Escolhas-chave do jogador</li>
          <li>Ponto sem volta</li>
        </ul>

        <p><strong>Ato 3: clímax e desfecho (20–25%)</strong></p>
        <ul>
          <li>O confronto final</li>
          <li>Segredos revelados</li>
          <li>Final (depende das escolhas)</li>
        </ul>

        <h3>Sistema de ramificações</h3>

        <p><strong>Estrutura linear com ramificações:</strong></p>
        <pre><code>Início {'→'} Escolha 1 {'→'} Evento A {'→'} Escolha 2 {'→'} Final 1/2/3
              {'→'} Evento B {'→'} Escolha 3 {'→'} Final 4/5</code></pre>

        <p><strong>Sistema de rotas:</strong></p>
        <pre><code>Prólogo comum {'→'} Escolha do personagem {'→'} Rota da Alice {'→'} Finais da Alice
                                       {'→'} Rota do David {'→'} Finais do David
                                       {'→'} Rota da Elena {'→'} Finais da Elena</code></pre>

        <h3>Tipos de escolhas</h3>

        <ul>
          <li><strong>De enredo</strong> — afetam o desenvolvimento da história</li>
          <li><strong>De personalidade</strong> — definem o caráter do herói</li>
          <li><strong>Românticas</strong> — desenvolvem as relações com os personagens</li>
          <li><strong>Morais</strong> — testam os valores do jogador</li>
          <li><strong>Cosméticas</strong> — não afetam a trama, mas aumentam a imersão</li>
        </ul>

        <h2>Etapa 5: a escrita do roteiro</h2>

        <h3>Formato de roteiro de VN</h3>

        <pre><code>[Local: Sala de aula, durante o dia]
[Música: school_calm.ogg]

Alice está perto da janela, olhando para o pátio de outono.

ALICE (pensativa)
&quot;Às vezes eu acho que tudo isso é só um sonho.&quot;

[O sprite da Alice muda para triste]

{'>'} Escolha:
1. &quot;Como assim?&quot; {'→'} goto scene_question
2. &quot;Eu entendo o que você quer dizer.&quot; {'→'} goto scene_understand
3. [Abraçá-la] {'→'} goto scene_hug (requer: afeto {'>'}= 5)

=== scene_question ===
PROTAGONISTA
&quot;Do que você está falando?&quot;

ALICE (surpresa)
&quot;Nada. Esquece.&quot;

[Alice vira o rosto]</code></pre>

        <h3>Dicas para escrever diálogos</h3>

        <p><strong>Deixe os diálogos naturais:</strong></p>
        <ul>
          <li>❌ &quot;Estou experimentando tristeza em decorrência do evento ocorrido&quot;</li>
          <li>✅ &quot;Eu... tô me sentindo péssima. Não consigo parar de pensar nisso.&quot;</li>
        </ul>

        <p><strong>Mostre a personalidade pela fala:</strong></p>
        <ul>
          <li>Um personagem grosseiro fala com frases curtas</li>
          <li>Um intelectual usa palavras complexas</li>
          <li>Uma pessoa tímida gagueja com frequência</li>
        </ul>

        <p><strong>Evite as &quot;cabeças falantes&quot;:</strong></p>
        <ul>
          <li>Acrescente ações entre as falas</li>
          <li>Descreva as reações dos personagens</li>
          <li>Use o ambiente</li>
        </ul>

        <h3>Notas técnicas</h3>

        <p>Inclua no roteiro para a equipe:</p>

        <pre><code>[CG: first_kiss.png] — ilustração especial
[SFX: door_slam.wav] — efeito sonoro
[Tela treme] — efeito visual
[Variável: trust_alice +1] — mudança de atributos</code></pre>

        <h2>Etapa 6: os finais</h2>

        <h3>Tipos de finais</h3>

        <ul>
          <li><strong>True End</strong> — o final &quot;verdadeiro&quot;, que revela a história inteira</li>
          <li><strong>Good End</strong> — um desfecho feliz para uma rota específica</li>
          <li><strong>Normal End</strong> — um final neutro</li>
          <li><strong>Bad End</strong> — um desfecho negativo</li>
          <li><strong>Secret End</strong> — um final oculto, liberado em condições especiais</li>
        </ul>

        <h3>Como criar finais satisfatórios</h3>

        <ol>
          <li><strong>Todo final deve fazer sentido</strong> — não castigue o jogador por escolhas &quot;erradas&quot;</li>
          <li><strong>Feche as pontas soltas</strong> — responda às perguntas que você levantou</li>
          <li><strong>Ressonância emocional</strong> — o final deve despertar emoções</li>
          <li><strong>Recompense a exploração</strong> — os bons finais exigem esforço</li>
        </ol>

        <h2>Criando novels na {BRAND}</h2>

        <p>Se você não quer lidar com programação e engines de jogos, a <a href={localePath('/')}>{BRAND}</a> oferece uma abordagem alternativa:</p>

        <h3>Como funciona</h3>

        <ol>
          <li><strong>Crie um cenário</strong> — descreva o mundo da sua novel</li>
          <li><strong>Configure os personagens</strong> — suas personalidades e papéis</li>
          <li><strong>Estabeleça as regras</strong> — o que pode acontecer na história</li>
          <li><strong>A IA dá vida a tudo</strong> — a inteligência artificial conduz a narrativa</li>
        </ol>

        <h3>Vantagens da plataforma</h3>

        <ul>
          <li><strong>Sem programação</strong> — não é preciso saber Ren&apos;Py nem Unity</li>
          <li><strong>Resultado instantâneo</strong> — sua história funciona na hora</li>
          <li><strong>Conteúdo sem fim</strong> — a IA gera novos acontecimentos</li>
          <li><strong>Teste ideias</strong> — valide seu conceito antes do desenvolvimento completo</li>
        </ul>

        <h3>Para quem é</h3>

        <ul>
          <li>Autores iniciantes que querem testar suas ideias</li>
          <li>Escritores que querem prototipar um cenário</li>
          <li>Qualquer pessoa que queira fazer uma novel sem equipe</li>
        </ul>

        <h2>Ferramentas para o desenvolvimento tradicional</h2>

        <h3>Engines de visual novel</h3>

        <p><strong>Ren&apos;Py</strong> (gratuito)</p>
        <ul>
          <li>A engine de VN mais popular</li>
          <li>Linguagem de script parecida com Python</li>
          <li>Comunidade grande</li>
        </ul>

        <p><strong>TyranoBuilder</strong> (pago)</p>
        <ul>
          <li>Editor visual</li>
          <li>Não exige programação</li>
          <li>Exportação para várias plataformas</li>
        </ul>

        <p><strong>Visual Novel Maker</strong> (pago)</p>
        <ul>
          <li>Editor dos criadores do RPG Maker</li>
          <li>Interface simples</li>
          <li>Boa documentação</li>
        </ul>

        <h3>Programas para escrever</h3>

        <ul>
          <li><strong>Twine</strong> — planejamento visual das ramificações</li>
          <li><strong>Scrivener</strong> — organização de textos longos</li>
          <li><strong>Google Docs</strong> — escrita colaborativa</li>
        </ul>

        <h2>Checklist antes de começar</h2>

        <ul>
          <li>Gênero e tom definidos</li>
          <li>Logline escrita</li>
          <li>Cenário desenvolvido</li>
          <li>Fichas dos personagens criadas</li>
          <li>Estrutura do enredo esboçada</li>
          <li>Sistema de ramificações planejado</li>
          <li>Tipos de finais definidos</li>
          <li>Ferramenta de desenvolvimento escolhida</li>
        </ul>

        <h2>Conclusão</h2>

        <p>Escrever o roteiro de uma visual novel é um processo criativo empolgante. Da ideia à história pronta, pode levar de semanas a anos, dependendo do tamanho do projeto.</p>

        <p>Se você está começando agora, tente criar uma novel curta, de 1–2 horas de jogo. Ou use a <a href={localePath('/')}>{BRAND}</a> para ver suas ideias ganharem vida na hora — sem programação e sem meses de desenvolvimento.</p>

        <p><strong>Sua história está esperando para ser contada.</strong></p>
      </>
    ),
  },
};

export default post;
